// src/shared/ui/breadcrumb/breadcrumb.tsx
import { HiChevronRight } from "react-icons/hi";
import { jsx, jsxs } from "react/jsx-runtime";
var Breadcrumb = ({ items, separator }) => {
  return /* @__PURE__ */ jsx("nav", { className: "flex gap-2.5 items-center rounded-md", children: items.map((item, index) => {
    const isLast = index === items.length - 1;
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5", children: [
      item.onClick ? /* @__PURE__ */ jsx(
        "button",
        {
          onClick: item.onClick,
          className: "text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors",
          children: item.label
        }
      ) : /* @__PURE__ */ jsx(
        "span",
        {
          className: `text-sm font-medium ${isLast ? "text-gray-500" : "text-gray-700"}`,
          children: item.label
        }
      ),
      !isLast && (separator || /* @__PURE__ */ jsx(HiChevronRight, { className: "w-3 h-3 text-gray-400" }))
    ] }, index);
  }) });
};

// src/shared/ui/badRequestAlert/badRequestAlert.tsx
import { Alert } from "flowbite-react";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var BadRequestAlert = ({
  message,
  details,
  className = "mt-6"
}) => {
  if (details.length === 0 && !message)
    return null;
  return /* @__PURE__ */ jsx2("div", { className, children: /* @__PURE__ */ jsx2(Alert, { color: "failure", children: /* @__PURE__ */ jsxs2("div", { className: "flex flex-col gap-2", children: [
    message && /* @__PURE__ */ jsx2("span", { children: message }),
    details.length > 0 && /* @__PURE__ */ jsx2("ul", { className: "list-inside list-disc space-y-1", children: details.map((detail, i) => /* @__PURE__ */ jsx2("li", { children: detail.value ?? detail.key }, detail.key ?? i)) })
  ] }) }) });
};

// src/shared/ui/fileAttachment/fileAttachment.tsx
import { Icon, Image } from "@acme/assets";
import { Fragment, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var getFileTypeText = (fileName) => {
  const safe = (fileName || "").trim();
  const lastDot = safe.lastIndexOf(".");
  if (lastDot === -1 || lastDot === safe.length - 1) {
    return "FILE";
  }
  return safe.slice(lastDot + 1).toLocaleUpperCase();
};
var getFileNameText = (fileName) => {
  const safe = (fileName || "").trim();
  const lastDot = safe.lastIndexOf(".");
  if (lastDot <= 0) {
    return safe;
  }
  return safe.slice(0, lastDot);
};
var FileAttachment = ({
  fileName,
  size,
  className = "",
  onClick
}) => {
  const nameText = getFileNameText(fileName) || "\u0412\u043B\u043E\u0436\u0435\u043D\u0438\u0435";
  const typeText = getFileTypeText(fileName);
  return /* @__PURE__ */ jsx3("div", { className: "p-4 bg-gray-100 rounded-r-md rounded-bl-md", children: /* @__PURE__ */ jsxs3(
    "div",
    {
      className: `flex items-center p-2 gap-4 rounded-lg bg-gray-50 ${className}`,
      children: [
        /* @__PURE__ */ jsxs3("div", { className: "flex flex-1 flex-col gap-2 min-w-0", children: [
          /* @__PURE__ */ jsxs3("div", { className: "flex gap-2 min-w-0", children: [
            /* @__PURE__ */ jsx3("div", { className: "text-lg shrink-0", children: /* @__PURE__ */ jsx3(Image, { name: "pdf", className: "h-5 w-5", alt: "File" }) }),
            /* @__PURE__ */ jsx3("div", { className: "text-sm font-medium truncate", children: nameText })
          ] }),
          /* @__PURE__ */ jsxs3("div", { className: "text-xs font-normal flex text-gray-500 items-center mt-2", children: [
            /* @__PURE__ */ jsx3("span", { children: typeText }),
            size && /* @__PURE__ */ jsxs3(Fragment, { children: [
              /* @__PURE__ */ jsx3("span", { children: "\u2022" }),
              /* @__PURE__ */ jsx3("span", { children: size })
            ] })
          ] })
        ] }),
        onClick && /* @__PURE__ */ jsx3(
          "button",
          {
            type: "button",
            onClick,
            className: "text-gray-500 hover:text-gray-700",
            "aria-label": "\u0421\u043A\u0430\u0447\u0430\u0442\u044C",
            children: /* @__PURE__ */ jsx3(Icon, { name: "download-outline", size: 16, alt: "\u0421\u043A\u0430\u0447\u0430\u0442\u044C" })
          }
        )
      ]
    }
  ) });
};
var FileAttachmentList = ({
  attachments,
  onDownload,
  className = ""
}) => {
  if (!attachments || attachments.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs3("div", { className: `flex flex-col ${className}`, children: [
    /* @__PURE__ */ jsx3("div", { className: "text-normal text-sm text-gray-500 mb-3 mt-6", children: "\u041F\u0440\u0438\u043A\u0440\u0435\u043F\u043B\u0435\u043D\u043D\u044B\u0435 \u0444\u0430\u0439\u043B\u044B" }),
    /* @__PURE__ */ jsx3("div", { className: "flex gap-4", children: attachments.map((attachment, idx) => /* @__PURE__ */ jsx3(
      FileAttachment,
      {
        fileName: attachment.fileName,
        size: attachment.size,
        onClick: () => onDownload(attachment.id || "", attachment.fileName || "")
      },
      attachment.id || `${attachment.fileName || "file"}-${idx}`
    )) })
  ] });
};

// src/shared/ui/fileUpload/fileUpload.tsx
import { useCallback as useCallback2, useEffect, useRef, useState as useState2 } from "react";
import { HiUpload, HiXCircle } from "react-icons/hi";
import { HiArrowPath, HiDocument } from "react-icons/hi2";

// src/shared/providers/useModal.ts
import { useContext } from "react";

// src/shared/providers/modalProvider.tsx
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  createTheme
} from "flowbite-react";
import { createContext, useCallback, useState } from "react";
import { HiX } from "react-icons/hi";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var ModalContext = createContext(
  void 0
);
var ModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState(void 0);
  const [modalType, setModalType] = useState(
    "simple"
  );
  const [okBtnText, setOkBtnText] = useState("\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C");
  const [cancelBtnText, setCancelBtnText] = useState("\u041E\u0442\u043C\u0435\u043D\u0430");
  const [opaqueBackground, setOpaqueBackground] = useState(false);
  const [modalClassName, setModalClassName] = useState("");
  const [containerWidth, setContainerWidth] = useState(416);
  const [size, setSize] = useState("md");
  const [sizeToContent, setSizeToContent] = useState(false);
  const [resolvePromise, setResolvePromise] = useState(null);
  const [onCloseCallback, setOnCloseCallback] = useState(
    null
  );
  const [onConfirmCb, setOnConfirmCb] = useState(null);
  const closeModal = useCallback(() => {
    setIsOpen(false);
    setModalContent(null);
    setModalTitle(void 0);
    setOpaqueBackground(false);
    setModalClassName("");
    setSizeToContent(false);
    setResolvePromise(null);
    setOnCloseCallback(null);
  }, []);
  const openModal = useCallback((config) => {
    setModalType(config.modalType || "simple");
    const requestedSize = config.size || "md";
    const isAuto = requestedSize === "auto";
    setSize(isAuto ? "md" : requestedSize);
    setSizeToContent(isAuto);
    setModalContent(config.content);
    setModalTitle(config.title);
    setOpaqueBackground(config.opaqueBackground || false);
    setOnCloseCallback(() => config.onClose || null);
    setContainerWidth(config.containerWidth || 416);
    setIsOpen(true);
    setModalClassName(config.modalClassName || "");
  }, []);
  const openConfirmModal = useCallback(
    (config) => {
      return new Promise((resolve) => {
        setModalType("confirm");
        setModalTitle(config.title);
        setModalContent(config.content);
        setOkBtnText(config.okBtn || "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C");
        setCancelBtnText(config.cancelBtn || "\u041E\u0442\u043C\u0435\u043D\u0430");
        setOpaqueBackground(config.opaqueBackground || false);
        setResolvePromise(() => resolve);
        setIsOpen(true);
        setModalClassName(config.modalClassName || "");
        setOnConfirmCb(() => config?.onConfirmCb);
      });
    },
    []
  );
  const handleConfirm = useCallback(() => {
    if (resolvePromise) {
      onConfirmCb?.();
      resolvePromise(true);
    }
    closeModal();
  }, [resolvePromise, closeModal]);
  const handleCancel = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(false);
    }
    closeModal();
  }, [resolvePromise, closeModal]);
  const handleClose = useCallback(() => {
    if (modalType === "confirm" && resolvePromise) {
      resolvePromise(false);
    }
    if (modalType === "simple" && onCloseCallback) {
      onCloseCallback();
    }
    closeModal();
  }, [modalType, resolvePromise, onCloseCallback, closeModal]);
  return /* @__PURE__ */ jsxs4(ModalContext.Provider, { value: { openModal, openConfirmModal, closeModal }, children: [
    children,
    opaqueBackground ? /* @__PURE__ */ jsx4("div", { className: "fixed inset-0 z-50 bg-gray-600 flex items-center justify-center", children: /* @__PURE__ */ jsxs4(
      "div",
      {
        className: "bg-white border border-gray-200 rounded-lg shadow-lg",
        style: { width: `${containerWidth}px` },
        children: [
          (modalType === "confirm" || modalTitle) && /* @__PURE__ */ jsxs4("div", { className: "flex items-center justify-between p-5 border-b border-gray-200", children: [
            modalTitle ? /* @__PURE__ */ jsx4("h3", { className: "text-xl font-medium text-gray-900", children: modalTitle }) : /* @__PURE__ */ jsx4("span", {}),
            /* @__PURE__ */ jsx4(
              "button",
              {
                onClick: handleClose,
                className: "text-gray-400 hover:text-gray-600",
                children: /* @__PURE__ */ jsx4(HiX, { className: "h-5 w-5" })
              }
            )
          ] }),
          /* @__PURE__ */ jsx4("div", { className: "p-5", children: /* @__PURE__ */ jsx4("div", { className: modalType === "confirm" ? "space-y-6" : "", children: typeof modalContent === "string" ? /* @__PURE__ */ jsx4("p", { className: "text-normal leading-relaxed text-gray-500", children: modalContent }) : modalContent }) }),
          modalType === "confirm" && /* @__PURE__ */ jsxs4("div", { className: "flex gap-4 items-center p-5 border-t border-gray-200", children: [
            /* @__PURE__ */ jsx4(Button, { color: "primary", onClick: handleConfirm, children: okBtnText }),
            /* @__PURE__ */ jsx4(Button, { color: "second", onClick: handleCancel, children: cancelBtnText })
          ] })
        ]
      }
    ) }) : /* @__PURE__ */ jsxs4(
      Modal,
      {
        show: isOpen,
        onClose: handleClose,
        dismissible: modalType !== "confirm",
        className: modalClassName,
        size,
        theme: sizeToContent ? createTheme({
          root: {
            sizes: {
              md: "w-max max-w-[90vw]"
            }
          }
        }) : void 0,
        children: [
          (modalType === "confirm" || modalTitle) && /* @__PURE__ */ jsx4(ModalHeader, { children: /* @__PURE__ */ jsx4("div", { className: "flex items-center justify-between w-full", children: modalTitle ? /* @__PURE__ */ jsx4("h3", { className: "text-xl font-medium text-gray-900 dark:text-white", children: modalTitle }) : /* @__PURE__ */ jsx4("span", {}) }) }),
          modalType === "simple" && !modalTitle && /* @__PURE__ */ jsx4("div", { className: "flex justify-end p-2", children: /* @__PURE__ */ jsx4(
            "button",
            {
              onClick: handleClose,
              className: "text-gray-400 hover:text-gray-900 dark:hover:text-white",
              children: /* @__PURE__ */ jsx4(HiX, { className: "h-5 w-5" })
            }
          ) }),
          /* @__PURE__ */ jsx4(ModalBody, { className: "overflow-visible", children: /* @__PURE__ */ jsx4(
            "div",
            {
              className: (modalType === "confirm" ? "space-y-6" : "") + (sizeToContent ? " flex flex-col items-center w-full" : ""),
              children: typeof modalContent === "string" ? /* @__PURE__ */ jsx4("p", { className: "text-base leading-relaxed text-gray-500 dark:text-gray-400", children: modalContent }) : modalContent
            }
          ) }),
          modalType === "confirm" && /* @__PURE__ */ jsxs4(ModalFooter, { children: [
            /* @__PURE__ */ jsx4(Button, { color: "primary", onClick: handleConfirm, children: okBtnText }),
            /* @__PURE__ */ jsx4(Button, { color: "second", onClick: handleCancel, children: cancelBtnText })
          ] })
        ]
      }
    )
  ] });
};

// src/shared/providers/useModal.ts
var useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};

// src/shared/ui/fileUpload/fileUpload.tsx
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
function validateFile(file, options) {
  const { maxSize, accept } = options;
  const maxSizeBytes = maxSize * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return `\u0424\u0430\u0439\u043B "${file.name}" \u043F\u0440\u0435\u0432\u044B\u0448\u0430\u0435\u0442 \u043C\u0430\u043A\u0441\u0438\u043C\u0430\u043B\u044C\u043D\u044B\u0439 \u0440\u0430\u0437\u043C\u0435\u0440 ${maxSize} \u041C\u0411`;
  }
  if (accept && accept !== "*") {
    const acceptedExtensions = accept.split(",").map((ext) => ext.trim());
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!acceptedExtensions.includes(fileExtension)) {
      return `\u0424\u0430\u0439\u043B "${file.name}" \u0438\u043C\u0435\u0435\u0442 \u043D\u0435\u0434\u043E\u043F\u0443\u0441\u0442\u0438\u043C\u044B\u0439 \u0444\u043E\u0440\u043C\u0430\u0442. \u0420\u0430\u0437\u0440\u0435\u0448\u0435\u043D\u043D\u044B\u0435 \u0444\u043E\u0440\u043C\u0430\u0442\u044B: ${accept}`;
    }
  }
  return null;
}
function formatTimeRemaining(seconds) {
  if (seconds < 60)
    return `${seconds} \u0441\u0435\u043A\u0443\u043D\u0434 \u043E\u0441\u0442\u0430\u043B\u043E\u0441\u044C`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const word = minutes === 1 ? "\u043C\u0438\u043D\u0443\u0442\u0430" : minutes < 5 ? "\u043C\u0438\u043D\u0443\u0442\u044B" : "\u043C\u0438\u043D\u0443\u0442";
  return remainingSeconds === 0 ? `${minutes} ${word} \u043E\u0441\u0442\u0430\u043B\u043E\u0441\u044C` : `${minutes} ${word} ${remainingSeconds} \u0441\u0435\u043A\u0443\u043D\u0434 \u043E\u0441\u0442\u0430\u043B\u043E\u0441\u044C`;
}
function getFileExtension(fileName) {
  return fileName.split(".").pop()?.toLowerCase() || "";
}
function FileIcon({ fileName }) {
  const isPdf = getFileExtension(fileName) === "pdf";
  return /* @__PURE__ */ jsx5(
    HiDocument,
    {
      className: `w-5 h-5 shrink-0 ${isPdf ? "text-red-500" : "text-gray-400"}`
    }
  );
}
function ProgressCircle({
  progress,
  isPaused
}) {
  const percentage = Math.min(100, Math.max(0, progress));
  const angle = percentage / 100 * 360;
  const color = isPaused ? "#9ca3af" : "#1c64f2";
  const conicGradient = `conic-gradient(from -90deg, ${color} ${angle}deg, #e5e7eb ${angle}deg)`;
  return /* @__PURE__ */ jsxs5("div", { className: "relative w-8 h-8 shrink-0", children: [
    /* @__PURE__ */ jsx5(
      "div",
      {
        className: "absolute inset-0 rounded-full",
        style: {
          background: conicGradient,
          mask: "radial-gradient(circle at center, transparent 11px, black 13px)",
          WebkitMask: "radial-gradient(circle at center, transparent 11px, black 13px)"
        }
      }
    ),
    isPaused && /* @__PURE__ */ jsx5("div", { className: "absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsx5(HiArrowPath, { className: "w-3 h-3 text-primary-600" }) })
  ] });
}
function CompletedFileItem({
  fileName,
  onRemove
}) {
  return /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [
    /* @__PURE__ */ jsx5(FileIcon, { fileName }),
    /* @__PURE__ */ jsx5("p", { className: "text-sm font-medium text-gray-900 truncate flex-1 min-w-0", children: fileName }),
    onRemove && /* @__PURE__ */ jsx5(
      "button",
      {
        onClick: onRemove,
        className: "text-gray-400 hover:text-red-600 shrink-0",
        title: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C",
        children: /* @__PURE__ */ jsx5(HiXCircle, { className: "w-4 h-4" })
      }
    )
  ] });
}
var FileUpload = ({
  onFileSelect,
  onFileRemove,
  onFileResume,
  accept = ".pdf,.docx,.png,.jpg,.jpeg",
  maxSize = 10,
  multiple = true,
  disabled = false,
  uploadingFiles = [],
  loadedFiles = []
}) => {
  const { openConfirmModal } = useModal();
  const [isDragging, setIsDragging] = useState2(false);
  const [internalFiles, setInternalFiles] = useState2([]);
  const fileInputRef = useRef(null);
  const displayFiles = uploadingFiles.length > 0 ? uploadingFiles : internalFiles;
  const isUncontrolled = uploadingFiles.length === 0;
  useEffect(() => {
    if (uploadingFiles.length > 0)
      setInternalFiles([]);
  }, [uploadingFiles.length]);
  useEffect(() => {
    if (isUncontrolled && onFileSelect && internalFiles.length > 0) {
      onFileSelect(internalFiles.map((u) => u.file));
    }
  }, [internalFiles, isUncontrolled, onFileSelect]);
  const handleFiles = useCallback2(
    (files) => {
      const validFiles = [];
      const errors = [];
      const opts = { maxSize, accept };
      files.forEach((file) => {
        const error = validateFile(file, opts);
        if (error)
          errors.push(error);
        else
          validFiles.push(file);
      });
      if (errors.length > 0) {
        console.warn("\u041D\u0435\u043A\u043E\u0442\u043E\u0440\u044B\u0435 \u0444\u0430\u0439\u043B\u044B \u043D\u0435 \u043F\u0440\u043E\u0448\u043B\u0438 \u0432\u0430\u043B\u0438\u0434\u0430\u0446\u0438\u044E:", errors);
      }
      if (validFiles.length === 0)
        return;
      if (uploadingFiles.length === 0) {
        setInternalFiles((prev) => [
          ...prev,
          ...validFiles.map((file, index) => ({
            id: `${Date.now()}-${index}`,
            file,
            status: "pending",
            progress: 0
          }))
        ]);
      } else {
        onFileSelect?.(validFiles);
      }
    },
    [onFileSelect, uploadingFiles.length, maxSize, accept]
  );
  const handleFileInputChange = useCallback2(
    (e) => {
      if (e.target.files) {
        handleFiles(Array.from(e.target.files));
        e.target.value = "";
      }
    },
    [handleFiles]
  );
  const handleRemove = useCallback2(
    async (fileId) => {
      const confirmed = await openConfirmModal({
        title: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442?",
        content: "\u0412\u044B \u0432\u0441\u0435\u0433\u0434\u0430 \u0441\u043C\u043E\u0436\u0435\u0442\u0435 \u0434\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u043D\u0435\u043E\u0431\u0445\u043E\u0434\u0438\u043C\u044B\u0435 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u044B \u0432 \u043B\u044E\u0431\u043E\u0435 \u0432\u0440\u0435\u043C\u044F",
        okBtn: "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C",
        cancelBtn: "\u041E\u0442\u043C\u0435\u043D\u0430"
      });
      if (confirmed) {
        if (uploadingFiles.length > 0) {
          onFileRemove?.(fileId);
        } else {
          setInternalFiles((prev) => prev.filter((f) => f.id !== fileId));
        }
      }
    },
    [openConfirmModal, uploadingFiles.length, onFileRemove]
  );
  const dropZoneClass = [
    "h-full min-h-[140px] flex flex-col items-center justify-center p-8 bg-gray-50 border-2 border-dashed rounded-lg",
    isDragging ? "border-primary-500 bg-primary-50" : "border-gray-200",
    disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
  ].join(" ");
  const hintText = accept?.includes(".pdf") && accept.split(",").length === 1 ? "\u041F\u0435\u0440\u0435\u0442\u0430\u0449\u0438\u0442\u0435 \u0441\u044E\u0434\u0430 PDF \u0444\u0430\u0439\u043B\u044B" : "\u041F\u0435\u0440\u0435\u0442\u0430\u0449\u0438\u0442\u0435 \u0441\u044E\u0434\u0430 PDF, DOCX, PNG \u0438\u043B\u0438 JPEG \u0444\u0430\u0439\u043B.";
  const hasFilesToShow = displayFiles.length > 0 || loadedFiles.length > 0;
  return /* @__PURE__ */ jsx5("div", { className: "w-full min-w-0", children: /* @__PURE__ */ jsxs5("div", { className: "flex flex-col gap-6 min-w-0 md:flex-row justify-center", children: [
    /* @__PURE__ */ jsx5("div", { className: "max-w-full grow", children: /* @__PURE__ */ jsxs5(
      "label",
      {
        htmlFor: "file-upload",
        onDragOver: (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled)
            setIsDragging(true);
        },
        onDragLeave: (e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
        },
        onDrop: (e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
          if (!disabled && e.dataTransfer.files.length > 0) {
            handleFiles(Array.from(e.dataTransfer.files));
          }
        },
        className: dropZoneClass,
        children: [
          /* @__PURE__ */ jsxs5("div", { className: "flex flex-col items-center gap-2", children: [
            /* @__PURE__ */ jsx5(HiUpload, { className: "w-5 h-5 text-gray-400" }),
            /* @__PURE__ */ jsxs5("div", { className: "text-sm font-semibold text-gray-500 text-center", children: [
              /* @__PURE__ */ jsx5("p", { children: hintText }),
              /* @__PURE__ */ jsxs5("p", { children: [
                "\u041C\u0430\u043A\u0441\u0438\u043C\u0430\u043B\u044C\u043D\u044B\u0439 \u0440\u0430\u0437\u043C\u0435\u0440 \u043A\u0430\u0436\u0434\u043E\u0433\u043E \u0444\u0430\u0439\u043B\u0430 ",
                maxSize,
                " \u041C\u0411"
              ] })
            ] }),
            /* @__PURE__ */ jsx5("p", { className: "text-xs text-primary-600 underline", children: "\u0438\u043B\u0438 \u0432\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0444\u0430\u0439\u043B" })
          ] }),
          /* @__PURE__ */ jsx5(
            "input",
            {
              ref: fileInputRef,
              id: "file-upload",
              type: "file",
              className: "hidden",
              accept,
              multiple,
              onChange: handleFileInputChange,
              disabled
            }
          )
        ]
      }
    ) }),
    hasFilesToShow && /* @__PURE__ */ jsxs5("div", { className: "flex flex-col gap-4 w-full min-w-0 shrink-0 md:w-auto md:min-w-[200px] md:max-w-[320px]", children: [
      displayFiles.map(
        (uf) => uf.status === "completed" ? /* @__PURE__ */ jsx5(
          CompletedFileItem,
          {
            fileName: uf.file.name,
            onRemove: () => handleRemove(uf.id)
          },
          uf.id
        ) : /* @__PURE__ */ jsxs5(
          "div",
          {
            className: "flex gap-3 items-center p-2 rounded",
            children: [
              /* @__PURE__ */ jsxs5("div", { className: "flex gap-3 items-center flex-1 min-w-0", children: [
                uf.status === "uploading" || uf.status === "paused" ? /* @__PURE__ */ jsx5(
                  ProgressCircle,
                  {
                    progress: uf.progress,
                    isPaused: uf.status === "paused"
                  }
                ) : /* @__PURE__ */ jsx5(FileIcon, { fileName: uf.file.name }),
                /* @__PURE__ */ jsxs5("div", { className: "flex-1 min-w-0", children: [
                  /* @__PURE__ */ jsx5("p", { className: "text-sm font-medium text-gray-900 truncate", children: uf.file.name }),
                  uf.status === "uploading" && /* @__PURE__ */ jsxs5("div", { className: "flex gap-2.5 items-center text-xs text-gray-500", children: [
                    /* @__PURE__ */ jsxs5("span", { children: [
                      Math.round(uf.progress),
                      "%"
                    ] }),
                    uf.progress > 0 && /* @__PURE__ */ jsx5("span", { children: formatTimeRemaining(
                      Math.round((100 - uf.progress) / 2)
                    ) })
                  ] }),
                  uf.status === "paused" && /* @__PURE__ */ jsxs5("div", { className: "flex gap-2.5 items-center text-xs text-gray-500", children: [
                    /* @__PURE__ */ jsxs5("span", { children: [
                      Math.round(uf.progress),
                      "%"
                    ] }),
                    /* @__PURE__ */ jsx5("span", { children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u043E\u0442\u043C\u0435\u043D\u0435\u043D\u0430" })
                  ] }),
                  uf.status === "error" && /* @__PURE__ */ jsx5("p", { className: "text-xs text-red-500", children: uf.error || "\u041E\u0448\u0438\u0431\u043A\u0430 \u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0438" })
                ] })
              ] }),
              /* @__PURE__ */ jsxs5("div", { className: "flex gap-3 items-center shrink-0", children: [
                uf.status === "paused" && /* @__PURE__ */ jsx5(
                  "button",
                  {
                    type: "button",
                    onClick: () => onFileResume?.(uf.id),
                    className: "text-primary-600 hover:text-primary-700",
                    title: "\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0443",
                    children: /* @__PURE__ */ jsx5(HiArrowPath, { className: "w-4 h-4" })
                  }
                ),
                /* @__PURE__ */ jsx5(
                  "button",
                  {
                    type: "button",
                    onClick: () => handleRemove(uf.id),
                    className: "text-gray-400 hover:text-red-600",
                    title: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C",
                    children: /* @__PURE__ */ jsx5(HiXCircle, { className: "w-4 h-4" })
                  }
                )
              ] })
            ]
          },
          uf.id
        )
      ),
      loadedFiles.map((loadedFile) => /* @__PURE__ */ jsx5(
        CompletedFileItem,
        {
          fileName: loadedFile.name,
          onRemove: loadedFile.onRemove
        },
        loadedFile.id
      ))
    ] })
  ] }) });
};

// src/shared/ui/input/input.tsx
import { TextInput } from "flowbite-react";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var Input = ({
  placeholder,
  value,
  onChange,
  type = "text",
  disabled = false,
  className = "",
  rightIcon,
  leftIcon
}) => {
  return /* @__PURE__ */ jsxs6("div", { className: `relative ${className}`, children: [
    leftIcon && /* @__PURE__ */ jsx6("div", { className: "absolute left-3 top-1/2 transform -translate-y-1/2", children: leftIcon }),
    /* @__PURE__ */ jsx6(
      TextInput,
      {
        type,
        placeholder,
        value,
        onChange: (e) => onChange?.(e.target.value),
        disabled,
        className: `w-full ${leftIcon ? "pl-10" : ""} ${rightIcon ? "pr-10" : ""}`
      }
    ),
    rightIcon && /* @__PURE__ */ jsx6("div", { className: "absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer", children: rightIcon })
  ] });
};

// src/shared/ui/passwordInput/passwordInput.tsx
import { TextInput as TextInput2 } from "flowbite-react";
import { useState as useState3 } from "react";
import { HiEye, HiEyeSlash } from "react-icons/hi2";
import { jsx as jsx7 } from "react/jsx-runtime";
var PasswordInput = ({
  id,
  name,
  value,
  onChange,
  placeholder,
  autoFocus,
  disabled,
  required,
  className = ""
}) => {
  const [visible, setVisible] = useState3(false);
  return /* @__PURE__ */ jsx7("div", { className: `w-72 relative ${className}`, children: /* @__PURE__ */ jsx7(
    TextInput2,
    {
      id,
      name,
      type: visible ? "text" : "password",
      value,
      onChange: (e) => onChange(e.target.value),
      placeholder,
      autoFocus,
      disabled,
      required,
      theme: {
        addon: "border-none bg-transparent p-0 m-0 w-0",
        field: {
          base: "relative",
          input: {
            withAddon: {
              on: "rounded-lg pl-10",
              off: "rounded-lg"
            }
          }
        }
      },
      addon: /* @__PURE__ */ jsx7(
        "button",
        {
          type: "button",
          "aria-label": visible ? "\u0421\u043A\u0440\u044B\u0442\u044C \u043F\u0430\u0440\u043E\u043B\u044C" : "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u043F\u0430\u0440\u043E\u043B\u044C",
          "aria-pressed": visible,
          onClick: () => setVisible((v) => !v),
          className: "text-gray-500 hover:text-gray-700 absolute left-3 top-1/2 -translate-y-1/2 z-10",
          children: visible ? /* @__PURE__ */ jsx7(HiEyeSlash, { className: "w-4 h-4" }) : /* @__PURE__ */ jsx7(HiEye, { className: "w-4 h-4" })
        }
      )
    }
  ) });
};

// src/shared/ui/select/select.tsx
import { Select as FlowbiteSelect } from "flowbite-react";
import { jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
var Select = ({
  options,
  value,
  onChange,
  placeholder = "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043E\u043F\u0446\u0438\u044E",
  disabled = false,
  className = ""
}) => {
  return /* @__PURE__ */ jsxs7(
    FlowbiteSelect,
    {
      value,
      onChange: (e) => onChange?.(e.target.value),
      disabled,
      className,
      children: [
        /* @__PURE__ */ jsx8("option", { value: "", disabled: true, children: placeholder }),
        options.map((option) => /* @__PURE__ */ jsx8("option", { value: String(option.value ?? ""), children: option.label ?? "" }, String(option.value ?? "")))
      ]
    }
  );
};

// src/shared/ui/dropdownSelect/dropdownSelect.tsx
import { Icon as Icon2 } from "@acme/assets";
import { createTheme as createTheme2, Dropdown, DropdownItem, TextInput as TextInput3 } from "flowbite-react";
import { jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
var customDropdownTheme = createTheme2({
  content: "p-2 cursor-pointer",
  floating: {
    base: "z-10 rounded-lg bg-white shadow-sm bg-red-500",
    content: "p-2 gap-1",
    target: "w-full",
    item: {
      base: "flex w-full cursor-pointer items-center justify-start text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white dark:focus:bg-gray-600 dark:focus:text-white",
      icon: "mr-2 h-4 w-4"
    }
  }
});
var customTextInputTheme = {
  field: {
    input: {
      base: "cursor-pointer"
    }
  }
};
var DropdownSelect = ({
  options,
  value,
  onChange,
  placeholder = "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043E\u043F\u0446\u0438\u044E",
  disabled = false,
  className = "",
  renderOption,
  clearable = false
}) => {
  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption ? selectedOption.displayLabel || (typeof selectedOption.label === "string" ? selectedOption.label : "") : "";
  const handleOptionClick = (optionValue) => {
    if (clearable && value && optionValue === value) {
      onChange?.("");
      return;
    }
    onChange?.(optionValue);
  };
  const defaultRenderOption = (option) => /* @__PURE__ */ jsx9("div", { children: option.label });
  return /* @__PURE__ */ jsx9("div", { className: `relative  cursor-pointer ${className}`, children: /* @__PURE__ */ jsx9(
    Dropdown,
    {
      theme: customDropdownTheme,
      label: "",
      dismissOnClick: true,
      renderTrigger: () => /* @__PURE__ */ jsxs8("div", { className: "relative group", children: [
        /* @__PURE__ */ jsx9(
          TextInput3,
          {
            type: "text",
            placeholder,
            value: displayValue,
            readOnly: true,
            disabled,
            theme: customTextInputTheme,
            className: "cursor-pointer"
          }
        ),
        /* @__PURE__ */ jsxs8("div", { className: "absolute right-3 top-1/2 -translate-y-1/2", children: [
          clearable && !!value ? /* @__PURE__ */ jsx9(
            "button",
            {
              type: "button",
              className: "hidden group-aria-expanded:flex items-center justify-center text-gray-500 hover:text-gray-700",
              "aria-label": "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C",
              onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange?.("");
              },
              children: /* @__PURE__ */ jsx9(Icon2, { name: "x-outline", size: 12 })
            }
          ) : null,
          /* @__PURE__ */ jsx9("span", { className: "flex items-center justify-center pointer-events-none group-aria-expanded:rotate-180 transition-transform", children: /* @__PURE__ */ jsx9(
            Icon2,
            {
              name: "chevron-down",
              size: 16,
              className: clearable && !!value ? "group-aria-expanded:hidden" : ""
            }
          ) })
        ] })
      ] }),
      children: /* @__PURE__ */ jsx9("div", { className: "flex flex-col gap-1 w-full max-h-[300px] overflow-y-auto", children: options.map((option) => /* @__PURE__ */ jsx9(
        DropdownItem,
        {
          className: "w-full px-3 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-100 cursor-pointer rounded-lg transition-colors",
          onClick: () => handleOptionClick(option.value),
          children: renderOption ? renderOption(option) : defaultRenderOption(option)
        },
        option.value
      )) })
    }
  ) });
};

// src/shared/ui/portalDropdown/portalDropdown.tsx
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from "@floating-ui/react";
import { createContext as createContext2, useContext as useContext2, useState as useState4 } from "react";
import { Fragment as Fragment2, jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var PortalDropdownContext = createContext2(
  null
);
var usePortalDropdown = () => {
  const context = useContext2(PortalDropdownContext);
  if (!context) {
    throw new Error(
      "usePortalDropdown must be used within PortalDropdown component"
    );
  }
  return context;
};
var PortalDropdown = ({
  trigger,
  children,
  placement = "bottom",
  dismissOnClick = false,
  className = "",
  onOpenChange
}) => {
  const [isOpen, setIsOpen] = useState4(false);
  const handleOpenChange = (open) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement,
    middleware: [offset(5), flip(), shift()],
    whileElementsMounted: autoUpdate
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context, {
      enabled: isOpen,
      outsidePress: true,
      escapeKey: true
    })
  ]);
  const handleContentClick = () => {
    if (dismissOnClick) {
      setIsOpen(false);
    }
  };
  const close = () => {
    handleOpenChange(false);
  };
  return /* @__PURE__ */ jsxs9(Fragment2, { children: [
    /* @__PURE__ */ jsx10(
      "div",
      {
        ref: refs.setReference,
        ...getReferenceProps(),
        className: "relative",
        children: trigger
      }
    ),
    isOpen && /* @__PURE__ */ jsx10(FloatingPortal, { children: /* @__PURE__ */ jsx10(
      "div",
      {
        ref: refs.setFloating,
        style: {
          ...floatingStyles,
          zIndex: 99999
        },
        className: `rounded-lg bg-white shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.05)] ${className}`,
        ...getFloatingProps(),
        onClick: handleContentClick,
        children: /* @__PURE__ */ jsx10(PortalDropdownContext.Provider, { value: { close }, children })
      }
    ) })
  ] });
};

// src/shared/ui/filterDropdown/filterDropdown.tsx
import { HiChevronDown } from "react-icons/hi";
import { jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
var FilterDropdownItem = ({
  option,
  isSelected,
  onSelect
}) => {
  const { close } = usePortalDropdown();
  return /* @__PURE__ */ jsx11(
    "button",
    {
      type: "button",
      className: `flex w-full items-center rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-100 ${isSelected ? "bg-gray-100 font-medium text-gray-900" : "text-gray-700"}`,
      onClick: () => {
        onSelect(option.value);
        close();
      },
      children: option.label
    }
  );
};
var FilterDropdown = (props) => {
  const selected = props.options.find((o) => o.value === props.value);
  const selectedLabel = selected?.label ?? "\u0412\u0441\u0435";
  return /* @__PURE__ */ jsx11(
    PortalDropdown,
    {
      dismissOnClick: false,
      className: "w-64 p-2",
      trigger: /* @__PURE__ */ jsxs10(
        "button",
        {
          type: "button",
          className: `flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm hover:bg-gray-50 ${props.className || ""}`,
          children: [
            /* @__PURE__ */ jsxs10("span", { className: "text-gray-500", children: [
              props.title,
              ":"
            ] }),
            /* @__PURE__ */ jsx11("span", { className: "truncate", children: selectedLabel }),
            /* @__PURE__ */ jsx11(HiChevronDown, { className: "h-4 w-4 shrink-0 text-gray-500" })
          ]
        }
      ),
      children: /* @__PURE__ */ jsx11("div", { className: "flex flex-col gap-1", children: props.options.map((option) => /* @__PURE__ */ jsx11(
        FilterDropdownItem,
        {
          option,
          isSelected: option.value === props.value,
          onSelect: props.onChange
        },
        `${option.value ?? "all"}`
      )) })
    }
  );
};

// src/shared/ui/skeleton/index.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
var Skeleton = ({ className }) => {
  return /* @__PURE__ */ jsx12("div", { className: `animate-pulse rounded-md bg-gray-200 ${className}` });
};

// src/shared/ui/tabs/tabs.tsx
import {
  createTheme as createTheme3,
  TabItem as FlowbiteTabItem,
  Tabs as FlowbiteTabs
} from "flowbite-react";
import { jsx as jsx13 } from "react/jsx-runtime";
var defaultTabsTheme = createTheme3({
  tabs: {
    base: "flex flex-col",
    tablist: {
      base: "flex gap-4 items-center bg-white p-4 rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]",
      tabitem: {
        base: "flex items-center justify-center px-4 py-2 p-0 rounded-lg text-sm font-normal leading-[1.5] first:ml-0 focus:outline-none disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-500",
        variant: {
          default: {
            base: "px-4 py-2",
            active: {
              on: "bg-brand-purple-900 text-white",
              off: "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            }
          }
        },
        icon: "mr-2 h-5 w-5"
      }
    },
    tabpanel: ""
  }
}).tabs;
var Tabs = ({
  items,
  defaultActiveKey,
  onTabChange,
  theme
}) => {
  return /* @__PURE__ */ jsx13(
    FlowbiteTabs,
    {
      "aria-label": "Tabs",
      variant: "default",
      theme: theme || defaultTabsTheme,
      onActiveTabChange: (tab) => onTabChange?.(items[tab]?.key),
      children: items.map((item, index) => /* @__PURE__ */ jsx13(
        FlowbiteTabItem,
        {
          title: item.title,
          active: defaultActiveKey === item.key || index === 0 && !defaultActiveKey,
          children: item.content
        },
        item.key
      ))
    }
  );
};

// src/shared/ui/textarea/textarea.tsx
import { Textarea as FlowbiteTextarea } from "flowbite-react";
import { jsx as jsx14, jsxs as jsxs11 } from "react/jsx-runtime";
var Textarea = ({
  label,
  placeholder,
  value,
  onChange,
  disabled = false,
  rows = 4,
  required = false,
  error,
  noResize
}) => {
  return /* @__PURE__ */ jsxs11("div", { className: "flex flex-col gap-2 w-full", children: [
    label && /* @__PURE__ */ jsxs11("label", { className: "text-sm font-medium text-gray-900", children: [
      label,
      required && /* @__PURE__ */ jsx14("span", { className: "text-red-600 ml-1", children: "*" })
    ] }),
    /* @__PURE__ */ jsx14(
      FlowbiteTextarea,
      {
        placeholder,
        value,
        onChange: (e) => onChange?.(e.target.value),
        disabled,
        rows,
        className: `w-full ${noResize && "resize-none"}`
      }
    ),
    error && /* @__PURE__ */ jsx14("p", { className: "text-sm text-red-600", children: error })
  ] });
};

// src/shared/ui/errorPage/errorPage.tsx
import { Button as Button2 } from "flowbite-react";
import { jsx as jsx15, jsxs as jsxs12 } from "react/jsx-runtime";
var errorConfig = {
  400: {
    defaultTitle: "\u041D\u0435\u0432\u0435\u0440\u043D\u044B\u0439 \u0437\u0430\u043F\u0440\u043E\u0441",
    defaultDescription: "\u0421\u0435\u0440\u0432\u0435\u0440 \u043D\u0435 \u0441\u043C\u043E\u0433 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u0430\u0442\u044C \u0432\u0430\u0448 \u0437\u0430\u043F\u0440\u043E\u0441. \u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0432\u0432\u0435\u0434\u0435\u043D\u043D\u044B\u0435 \u0434\u0430\u043D\u043D\u044B\u0435 \u0438 \u043F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u0441\u043D\u043E\u0432\u0430.",
    defaultActionLabel: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C\u0441\u044F \u043D\u0430\u0437\u0430\u0434"
  },
  401: {
    defaultTitle: "\u041D\u0435\u0430\u0432\u0442\u043E\u0440\u0438\u0437\u043E\u0432\u0430\u043D",
    defaultDescription: "\u0414\u043B\u044F \u0434\u043E\u0441\u0442\u0443\u043F\u0430 \u043A \u044D\u0442\u043E\u0439 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0435 \u043D\u0435\u043E\u0431\u0445\u043E\u0434\u0438\u043C\u043E \u0430\u0432\u0442\u043E\u0440\u0438\u0437\u043E\u0432\u0430\u0442\u044C\u0441\u044F. \u041F\u043E\u0436\u0430\u043B\u0443\u0439\u0441\u0442\u0430, \u0432\u043E\u0439\u0434\u0438\u0442\u0435 \u0432 \u0441\u0438\u0441\u0442\u0435\u043C\u0443.",
    defaultActionLabel: "\u0412\u043E\u0439\u0442\u0438"
  },
  403: {
    defaultTitle: "\u0414\u043E\u0441\u0442\u0443\u043F \u0437\u0430\u043F\u0440\u0435\u0449\u0435\u043D",
    defaultDescription: "\u0423 \u0432\u0430\u0441 \u043D\u0435\u0442 \u043F\u0440\u0430\u0432 \u0434\u043B\u044F \u0434\u043E\u0441\u0442\u0443\u043F\u0430 \u043A \u044D\u0442\u043E\u043C\u0443 \u0440\u0435\u0441\u0443\u0440\u0441\u0443. \u041E\u0431\u0440\u0430\u0442\u0438\u0442\u0435\u0441\u044C \u043A \u0430\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0430\u0442\u043E\u0440\u0443 \u0434\u043B\u044F \u043F\u043E\u043B\u0443\u0447\u0435\u043D\u0438\u044F \u0434\u043E\u0441\u0442\u0443\u043F\u0430.",
    defaultActionLabel: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C\u0441\u044F \u043D\u0430 \u0433\u043B\u0430\u0432\u043D\u0443\u044E"
  },
  404: {
    defaultTitle: "\u0421\u0442\u0440\u0430\u043D\u0438\u0446\u0430 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430",
    defaultDescription: "\u0412\u043E\u0437\u043C\u043E\u0436\u043D\u043E, \u043E\u043D\u0430 \u0431\u044B\u043B\u0430 \u0443\u0434\u0430\u043B\u0435\u043D\u0430 \u0438\u043B\u0438 \u0432\u044B \u0434\u043E\u043F\u0443\u0441\u0442\u0438\u043B\u0438 \u043E\u0448\u0438\u0431\u043A\u0443 \u0432 \u0430\u0434\u0440\u0435\u0441\u0435",
    defaultActionLabel: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C\u0441\u044F \u043D\u0430 \u0433\u043B\u0430\u0432\u043D\u0443\u044E"
  },
  500: {
    defaultTitle: "\u0412\u043D\u0443\u0442\u0440\u0435\u043D\u043D\u044F\u044F \u043E\u0448\u0438\u0431\u043A\u0430 \u0441\u0435\u0440\u0432\u0435\u0440\u0430",
    defaultDescription: "\u041D\u0430 \u0441\u0435\u0440\u0432\u0435\u0440\u0435 \u043F\u0440\u043E\u0438\u0437\u043E\u0448\u043B\u0430 \u043E\u0448\u0438\u0431\u043A\u0430. \u041C\u044B \u0443\u0436\u0435 \u0440\u0430\u0431\u043E\u0442\u0430\u0435\u043C \u043D\u0430\u0434 \u0438\u0441\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u0435\u043C \u043F\u0440\u043E\u0431\u043B\u0435\u043C\u044B. \u041F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u043F\u043E\u0437\u0436\u0435.",
    defaultActionLabel: "\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0443"
  }
};
var ErrorPage = ({
  statusCode,
  title,
  description,
  actionLabel,
  onAction,
  className = ""
}) => {
  const config = errorConfig[statusCode];
  return /* @__PURE__ */ jsx15(
    "div",
    {
      className: `flex flex-col items-center justify-center min-h-[60vh] gap-6 px-4 text-center ${className}`,
      children: /* @__PURE__ */ jsxs12("div", { className: "flex flex-col gap-4 max-w-md", children: [
        /* @__PURE__ */ jsx15("div", { className: "text-6xl font-bold text-gray-400", children: statusCode }),
        /* @__PURE__ */ jsxs12("div", { className: "flex flex-col gap-2", children: [
          /* @__PURE__ */ jsx15("h1", { className: "text-2xl font-semibold text-gray-900", children: title || config.defaultTitle }),
          /* @__PURE__ */ jsx15("p", { className: "text-gray-600", children: description || config.defaultDescription })
        ] }),
        onAction && /* @__PURE__ */ jsx15("div", { className: "flex justify-center mt-2", children: /* @__PURE__ */ jsx15(Button2, { color: "primary", onClick: onAction, children: actionLabel || config.defaultActionLabel }) })
      ] })
    }
  );
};

// src/shared/ui/field/field.tsx
import { Tooltip } from "flowbite-react";
import { Link } from "react-router-dom";
import { jsx as jsx16, jsxs as jsxs13 } from "react/jsx-runtime";
var ELLIPSIS = "\u2026";
var hasText = (v) => typeof v === "string" && v.trim().length > 0;
var renderFieldIfText = (label, value, props) => hasText(value) ? /* @__PURE__ */ jsx16(Field, { label, value, ...props }) : null;
var truncate = (text, maxLength) => text.length <= maxLength ? text : text.slice(0, maxLength) + ELLIPSIS;
var Field = ({
  label,
  value,
  className = "",
  align = "right",
  link,
  maxLength
}) => {
  const isString = typeof value === "string";
  const shouldTruncate = maxLength != null && isString && value.length > maxLength;
  const displayValue = shouldTruncate ? truncate(value, maxLength) : value ?? "-";
  const fullText = shouldTruncate && isString ? value : void 0;
  const valueContent = link ? /* @__PURE__ */ jsx16(Link, { className: "text-primary-600 hover:text-primary-600/80", to: link, children: displayValue }) : /* @__PURE__ */ jsx16("div", { className: "font-medium truncate", children: displayValue });
  const wrappedValue = fullText != null ? /* @__PURE__ */ jsxs13(Tooltip, { content: fullText, children: [
    " ",
    valueContent
  ] }) : valueContent;
  return /* @__PURE__ */ jsxs13(
    "div",
    {
      className: `${className} flex flex-col gap-1 ${align === "left" ? "items-end" : ""}`,
      children: [
        /* @__PURE__ */ jsx16("div", { className: "text-gray-500 text-sm", children: label }),
        /* @__PURE__ */ jsx16("div", { className: "min-w-0", children: wrappedValue })
      ]
    }
  );
};

// src/shared/ui/separator/separator.tsx
import { twMerge } from "tailwind-merge";
import { jsx as jsx17 } from "react/jsx-runtime";
var Separator = ({ className, vertical }) => {
  return /* @__PURE__ */ jsx17(
    "div",
    {
      className: twMerge(
        `border-border-border border-t border-solid`,
        vertical && "border-t-none border-l",
        className
      )
    }
  );
};

// src/shared/ui/rangeDropdown/rangeDropdown.tsx
import { Button as Button3, Dropdown as Dropdown2, DropdownDivider, TextInput as TextInput4 } from "flowbite-react";
import { useMemo, useState as useState5 } from "react";
import { HiChevronDown as HiChevronDown2 } from "react-icons/hi";
import { jsx as jsx18, jsxs as jsxs14 } from "react/jsx-runtime";
var RangeDropdown = (props) => {
  const [fromValue, setFromValue] = useState5(props.value?.from || "");
  const [fromValueLabel, setFromValueLabel] = useState5(props.value?.from || "");
  const [toValue, setToValue] = useState5(props.value?.to || "");
  const [toValueLabel, setToValueLabel] = useState5(props.value?.to || "");
  const [isApplyClicked, setIsApplyClicked] = useState5(false);
  const TRIGGER_ID = "dropdown-trigger-button";
  const handleApply = () => {
    const triggerButton = document.getElementById(TRIGGER_ID);
    setFromValueLabel(fromValue);
    setToValueLabel(toValue);
    setIsApplyClicked(true);
    props.onChange && props.onChange({ from: fromValue, to: toValue });
    if (triggerButton) {
      triggerButton.click();
    }
  };
  const selectedText = useMemo(() => {
    if (fromValueLabel && toValueLabel)
      return `\u041E\u0442 ${fromValueLabel} \u0434\u043E ${toValueLabel}`;
    if (fromValueLabel)
      return `\u041E\u0442 ${fromValueLabel}`;
    if (toValueLabel)
      return `\u0414\u043E ${toValueLabel}`;
    return "-";
  }, [fromValueLabel, toValueLabel]);
  const handleKeyDown = (e) => {
    e.stopPropagation();
  };
  return /* @__PURE__ */ jsxs14("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx18("div", { className: "text-sm font-medium text-gray-900", children: props.label }),
    /* @__PURE__ */ jsx18(
      Dropdown2,
      {
        className: "max-w-fit",
        color: "second",
        label: props.placeholder,
        dismissOnClick: false,
        renderTrigger: () => /* @__PURE__ */ jsxs14(
          Button3,
          {
            color: "second",
            className: "flex justify-between px-3 py-2",
            id: TRIGGER_ID,
            children: [
              /* @__PURE__ */ jsx18("div", { className: "truncate flex", children: /* @__PURE__ */ jsx18("div", { className: "text-gray-900 mr-1", children: isApplyClicked || props.value ? selectedText : props.placeholder }) }),
              /* @__PURE__ */ jsx18("div", { className: "flex items-center", children: /* @__PURE__ */ jsx18(HiChevronDown2, { className: "w-5 h-5 text-gray-900" }) })
            ]
          }
        ),
        children: /* @__PURE__ */ jsxs14("div", { className: "p-3 w-64 space-y-3", children: [
          /* @__PURE__ */ jsx18("div", { className: "text-sm font-medium text-gray-900 dark:text-white", children: props.placeholder }),
          /* @__PURE__ */ jsx18(DropdownDivider, {}),
          /* @__PURE__ */ jsx18(
            TextInput4,
            {
              id: "from-input",
              type: "number",
              placeholder: "\u041E\u0442:",
              value: fromValue,
              onChange: (e) => setFromValue(e.target.value),
              onKeyDown: handleKeyDown
            }
          ),
          /* @__PURE__ */ jsx18(
            TextInput4,
            {
              id: "to-input",
              type: "number",
              placeholder: "\u0414\u043E:",
              value: toValue,
              onChange: (e) => setToValue(e.target.value),
              onKeyDown: handleKeyDown
            }
          ),
          /* @__PURE__ */ jsx18("div", { className: "pt-2", children: /* @__PURE__ */ jsx18(
            Button3,
            {
              onClick: () => handleApply(),
              color: "primary",
              className: "w-full justify-center",
              disabled: !fromValue && !toValue,
              children: "\u041F\u0440\u0438\u043C\u0435\u043D\u0438\u0442\u044C"
            }
          ) })
        ] })
      }
    )
  ] });
};

// src/shared/ui/rangeDropdown/rangeDropdownFilter.tsx
import { Button as Button4, Dropdown as Dropdown3, DropdownDivider as DropdownDivider2, TextInput as TextInput5 } from "flowbite-react";
import { useMemo as useMemo2, useState as useState6 } from "react";
import { HiChevronDown as HiChevronDown3 } from "react-icons/hi";
import { jsx as jsx19, jsxs as jsxs15 } from "react/jsx-runtime";
var RangeDropdownFilter = (props) => {
  const [fromValue, setFromValue] = useState6("");
  const [fromValueLabel, setFromValueLabel] = useState6("");
  const [toValue, setToValue] = useState6("");
  const [toValueLabel, setToValueLabel] = useState6("");
  const TRIGGER_ID = "dropdown-trigger-filter-button";
  const handleApply = () => {
    const triggerButton = document.getElementById(TRIGGER_ID);
    setFromValueLabel(fromValue);
    setToValueLabel(toValue);
    props.onChange && props.onChange({ from: fromValue, to: toValue });
    if (triggerButton) {
      triggerButton.click();
    }
  };
  const handleReset = (e) => {
    e.stopPropagation();
    setFromValue("");
    setToValueLabel("");
    setFromValueLabel("");
    setToValue("");
    props.onChange && props.onChange("\u0412\u0441\u0435");
  };
  const labelText = useMemo2(() => {
    if (fromValueLabel && toValueLabel)
      return `${fromValueLabel} - ${toValueLabel}`;
    if (fromValueLabel)
      return `\u043E\u0442 ${fromValueLabel}`;
    if (toValueLabel)
      return `\u0434\u043E ${toValueLabel}`;
    return "\u0412\u0441\u0435";
  }, [fromValueLabel, toValueLabel]);
  const handleKeyDown = (e) => {
    e.stopPropagation();
  };
  return /* @__PURE__ */ jsx19(
    Dropdown3,
    {
      className: "max-w-fit",
      color: "second",
      label: props.label,
      dismissOnClick: false,
      renderTrigger: () => /* @__PURE__ */ jsxs15(Button4, { color: "second", className: "px-3 py-2", id: TRIGGER_ID, children: [
        /* @__PURE__ */ jsxs15("div", { className: "truncate flex", children: [
          /* @__PURE__ */ jsxs15("div", { className: "text-gray-500 mr-1", children: [
            props.label,
            ":"
          ] }),
          /* @__PURE__ */ jsx19("div", { className: "text-gray-900 mr-1", children: labelText })
        ] }),
        /* @__PURE__ */ jsx19("div", { className: "flex items-center", children: fromValueLabel || toValueLabel ? /* @__PURE__ */ jsx19("div", { onClick: handleReset, children: "\u2715" }) : /* @__PURE__ */ jsx19(
          HiChevronDown3,
          {
            className: "w-5 h-5 text-gray-900"
          }
        ) })
      ] }),
      children: /* @__PURE__ */ jsxs15("div", { className: "p-3 w-64 space-y-3", children: [
        /* @__PURE__ */ jsx19("div", { className: "text-sm font-medium text-gray-900 dark:text-white", children: props.label }),
        /* @__PURE__ */ jsx19(DropdownDivider2, {}),
        /* @__PURE__ */ jsx19(
          TextInput5,
          {
            id: "from-input",
            type: "number",
            placeholder: "\u041E\u0442:",
            value: fromValue,
            onChange: (e) => setFromValue(e.target.value),
            onKeyDown: handleKeyDown
          }
        ),
        /* @__PURE__ */ jsx19(
          TextInput5,
          {
            id: "to-input",
            type: "number",
            placeholder: "\u0414\u043E:",
            value: toValue,
            onChange: (e) => setToValue(e.target.value),
            onKeyDown: handleKeyDown
          }
        ),
        /* @__PURE__ */ jsx19("div", { className: "pt-2", children: /* @__PURE__ */ jsx19(
          Button4,
          {
            onClick: () => handleApply(),
            color: "primary",
            className: "w-full justify-center",
            disabled: !fromValue && !toValue,
            children: "\u041F\u0440\u0438\u043C\u0435\u043D\u0438\u0442\u044C"
          }
        ) })
      ] })
    }
  );
};

// src/shared/ui/customSelectDropdown/customSelectDropdown.tsx
import { useState as useState7 } from "react";
import { Button as Button5, Dropdown as Dropdown4, DropdownItem as DropdownItem2 } from "flowbite-react";
import { HiChevronDown as HiChevronDown4 } from "react-icons/hi";
import { jsx as jsx20, jsxs as jsxs16 } from "react/jsx-runtime";
var CustomSelectDropdown = (props) => {
  const [selected, setSelected] = useState7(
    props.defaultOption
  );
  const [isOpen, setIsOpen] = useState7(false);
  const isDefaultSelected = selected?.label === props.defaultOption?.label;
  const handleSelect = (option) => {
    setSelected(option);
    props.onChange(option.value);
    setIsOpen(false);
  };
  const handleReset = (e) => {
    e.stopPropagation();
    setSelected(props.defaultOption);
    props.onChange(props.defaultOption.label);
  };
  return /* @__PURE__ */ jsx20(
    Dropdown4,
    {
      label: "",
      renderTrigger: () => /* @__PURE__ */ jsxs16(Button5, { color: "second", className: "px-3 py-2", children: [
        /* @__PURE__ */ jsxs16("div", { className: "truncate flex", children: [
          /* @__PURE__ */ jsxs16("div", { className: "text-gray-500 mr-1", children: [
            props.label,
            ":"
          ] }),
          /* @__PURE__ */ jsx20("div", { className: "text-gray-900 mr-1", children: selected?.label })
        ] }),
        /* @__PURE__ */ jsx20("div", { className: "flex items-center", children: !isDefaultSelected ? /* @__PURE__ */ jsx20("div", { onClick: handleReset, children: "\u2715" }) : /* @__PURE__ */ jsx20(HiChevronDown4, { className: "w-5 h-5 text-gray-900" }) })
      ] }),
      children: props.options.map((option) => /* @__PURE__ */ jsx20(
        DropdownItem2,
        {
          onClick: () => handleSelect(option),
          className: "text-gray-900",
          children: /* @__PURE__ */ jsx20("div", { className: "flex items-center", children: option.label })
        },
        option.label
      ))
    }
  );
};

// src/shared/ui/copyableText/copyableText.tsx
import { Icon as Icon3 } from "@acme/assets";
import { Button as Button6 } from "flowbite-react";
import { useCallback as useCallback3, useState as useState8 } from "react";
import { twMerge as twMerge2 } from "tailwind-merge";
import { jsx as jsx21, jsxs as jsxs17 } from "react/jsx-runtime";
var CopyableText = ({
  value,
  label,
  className = "",
  classNameButton
}) => {
  const [copied, setCopied] = useState8(false);
  const handleCopy = useCallback3(() => {
    if (value == null || value === "")
      return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2e3);
    });
  }, [value]);
  if (value == null || value === "") {
    return /* @__PURE__ */ jsx21("span", { className: "text-gray-400", children: "\u2014" });
  }
  return /* @__PURE__ */ jsxs17("div", { className: `relative flex items-start gap-2 ${className}`, children: [
    /* @__PURE__ */ jsx21("div", { className: "break-all text-gray-900", children: label ?? value }),
    /* @__PURE__ */ jsxs17("div", { className: "relative shrink-0", children: [
      /* @__PURE__ */ jsx21(
        Button6,
        {
          color: "green",
          size: "xs",
          className: twMerge2(
            "shrink-0 bg-green-100 text-green-900 hover:bg-green-50",
            classNameButton
          ),
          onClick: handleCopy,
          title: "\u041A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C",
          children: /* @__PURE__ */ jsx21(Icon3, { name: "copy-outline", size: 16 })
        }
      ),
      copied && /* @__PURE__ */ jsx21(
        "div",
        {
          className: "absolute left-1/2 top-full z-10 mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white",
          role: "tooltip",
          children: "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u043D\u043E"
        }
      )
    ] })
  ] });
};

// src/shared/ui/pagination/pagination.tsx
import { Pagination as FlowbitePagination } from "flowbite-react";
import { jsx as jsx22, jsxs as jsxs18 } from "react/jsx-runtime";
var Pagination = ({
  pageNumber,
  pageSize,
  totalCount,
  onPageChange,
  isLoading = false,
  className = "",
  showInfo = true,
  nextLabel = "\u0412\u043F\u0435\u0440\u0451\u0434",
  previousLabel = "\u041D\u0430\u0437\u0430\u0434"
}) => {
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  if (totalCount <= pageSize) {
    return null;
  }
  const from = (pageNumber - 1) * pageSize + 1;
  const to = Math.min(pageNumber * pageSize, totalCount);
  const handlePageChange = (page) => {
    if (!isLoading)
      onPageChange(page);
  };
  return /* @__PURE__ */ jsxs18(
    "div",
    {
      className: `flex items-center justify-between border-t border-gray-200 pt-4 ${className}`,
      children: [
        showInfo && /* @__PURE__ */ jsxs18("div", { className: "text-sm text-gray-500", children: [
          "\u041F\u043E\u043A\u0430\u0437\u0430\u043D\u043E ",
          from,
          "\u2013",
          to,
          " \u0438\u0437 ",
          totalCount
        ] }),
        /* @__PURE__ */ jsx22(
          FlowbitePagination,
          {
            currentPage: pageNumber,
            totalPages,
            onPageChange: handlePageChange,
            previousLabel,
            nextLabel,
            showIcons: true,
            layout: "pagination",
            className: isLoading ? "pointer-events-none opacity-50" : ""
          }
        )
      ]
    }
  );
};

// src/shared/ui/pagination/paginationWithEllipsis.tsx
import { useState as useState9, useEffect as useEffect2 } from "react";

// src/shared/ui/pagination/utils.ts
var getPages = (activePage, numberPages, visibleNear = 2) => {
  const pages = [];
  let startPage = activePage - visibleNear;
  if (startPage <= 0) {
    startPage = 1;
  } else if (activePage + visibleNear > numberPages && startPage > 2) {
    startPage -= activePage - numberPages + visibleNear;
  }
  if (startPage > 2) {
    pages.push("1", "...");
  } else if (startPage === 2) {
    pages.push("1");
  }
  for (let page = startPage; page < visibleNear * 2 + 1 + startPage; page++) {
    if (page > numberPages)
      break;
    pages.push(String(page));
  }
  const lastPage = Number(pages.at(-1));
  if (lastPage < numberPages - 1) {
    pages.push("...", String(numberPages));
  } else if (lastPage === numberPages - 1) {
    pages.push(String(numberPages));
  }
  return pages;
};
var getActivePage = (startPage, numberPages) => {
  if (startPage < 0 || startPage > numberPages) {
    return 1;
  }
  return startPage;
};

// src/shared/ui/pagination/paginationWithEllipsis.tsx
import { Button as Button7, createTheme as createTheme4 } from "flowbite-react";
import { twMerge as twMerge3 } from "tailwind-merge";
import { Icon as Icon4 } from "@acme/assets";

// src/shared/utils/deepMerge.ts
var isObject = (item) => {
  return item === Object(item) && !Array.isArray(item);
};
var deepMerge = (target, ...sources) => {
  if (!sources.length)
    return target;
  const source = sources.shift();
  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key])
          Object.assign(target, { [key]: {} });
        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }
  return deepMerge(target, ...sources);
};

// src/shared/utils/date/formatDate.ts
var formatDate = (dateString) => {
  if (!dateString)
    return "\u2014";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(date);
};

// src/shared/utils/date/formatDateTime.ts
var formatDateTime = (dateString) => {
  if (!dateString)
    return "";
  const date = new Date(dateString);
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// src/shared/utils/date/formatRelativeDayLabel.ts
var formatRelativeDayLabel = ({
  date,
  locale = "ru-RU",
  todayLabel = "\u0421\u0435\u0433\u043E\u0434\u043D\u044F",
  yesterdayLabel = "\u0412\u0447\u0435\u0440\u0430",
  now = /* @__PURE__ */ new Date()
}) => {
  const today = now;
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) {
    return todayLabel;
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return yesterdayLabel;
  }
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
};

// src/shared/utils/date/formatMonthYear.ts
var formatMonthYear = (dateString) => {
  if (!dateString) {
    return "\u2014";
  }
  const date = new Date(dateString);
  const month = new Intl.DateTimeFormat("ru-RU", {
    month: "long"
  }).format(date);
  const year = date.getFullYear();
  return `${month} ${year}`;
};

// src/shared/utils/currency/formatNumber.ts
var numberFormat = new Intl.NumberFormat("ru-RU", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0
});
var formatNumber = (value) => value != null && Number.isFinite(value) ? numberFormat.format(value) : "\u2014";

// src/shared/utils/currency/getCurrencySymbol.ts
var getCurrencySymbol = (currency) => {
  return (currency?.code ?? "").trim() || "";
};

// src/shared/utils/file/download.ts
var downloadFile = ({
  data,
  fileName,
  mimeType = "application/pdf"
}) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// src/shared/utils/processFormData.ts
var processFormData = (data) => {
  const files = Object.entries(data).filter(([key]) => key.startsWith("file-")).map(([, value]) => value);
  const formData = Object.entries(data).filter(([key]) => !key.startsWith("file-")).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  return { files, formData };
};

// src/shared/utils/formatAddress.ts
var leftDefaultLenAddress = 5;
var rightDefaultLenAddress = 3;
var formatAddress = (address, options) => {
  if (!address) {
    return "";
  }
  const leftLen = options?.leftLen ?? leftDefaultLenAddress;
  const rightLen = options?.rightLen ?? rightDefaultLenAddress;
  if (leftLen < 0 || rightLen < 0) {
    return address;
  }
  if (rightLen > leftLen) {
    return address;
  }
  return `${address.slice(0, leftLen)}...${address.slice(-rightLen)}`;
};

// src/shared/ui/pagination/paginationWithEllipsis.tsx
import { jsx as jsx23, jsxs as jsxs19 } from "react/jsx-runtime";
var arrowButtonTheme = createTheme4({
  outlineColor: {
    gray: "border-gray-200 hover:text-gray-700 hover:bg-gray-200 hover:border-gray-200"
  }
});
var pageTheme = createTheme4({
  color: {
    purple: "bg-transparent text-gray-500 hover:bg-brand-purple-50 hover:text-brand-purple-900 focus:ring-brand-purple-100 focus:ring-2"
  }
});
var PaginationWithEllipsis = ({
  pageSize = 20,
  total,
  defaultCurrent = 1,
  currentPage,
  onChange,
  className
}) => {
  const numberPages = Math.ceil(total / pageSize);
  const [activePage, setActivePage] = useState9(
    getActivePage(defaultCurrent, numberPages)
  );
  const pages = getPages(activePage, numberPages);
  useEffect2(() => {
    if (currentPage) {
      const page = getActivePage(currentPage, numberPages);
      setActivePage(page);
    }
  }, [currentPage, numberPages]);
  const handleClickPage = (event) => {
    const value = Number(event.target.value);
    if (value !== activePage) {
      setActivePage(value);
      onChange?.({ page: value });
    }
  };
  const handleClickArrow = (increase) => () => {
    setActivePage((prev) => prev + increase);
    onChange?.({ page: activePage + increase });
  };
  return /* @__PURE__ */ jsxs19(
    "div",
    {
      className: twMerge3(
        "flex justify-between gap-2 border-t border-gray-200 pt-5",
        className
      ),
      role: "navigation",
      "aria-label": "Pagination Navigation",
      children: [
        /* @__PURE__ */ jsx23(
          Button7,
          {
            "aria-label": "move-left",
            outline: true,
            theme: arrowButtonTheme,
            color: "gray",
            onClick: handleClickArrow(-1),
            disabled: activePage === 1,
            children: /* @__PURE__ */ jsxs19("div", { className: "flex gap-2 items-center", children: [
              /* @__PURE__ */ jsx23(Icon4, { name: "arrow-back" }),
              "\u041F\u0440\u0435\u0434\u044B\u0434\u0443\u0449\u0430\u044F"
            ] })
          }
        ),
        /* @__PURE__ */ jsx23("nav", { className: "flex", children: pages.map((item, index) => {
          if (!Number(item)) {
            return /* @__PURE__ */ jsx23(
              "div",
              {
                className: "h-10 w-10 flex items-center justify-center text-gray-500",
                children: item
              },
              `${index}-ellipses`
            );
          }
          const isActive = activePage === Number(item);
          return /* @__PURE__ */ jsx23(
            Button7,
            {
              theme: pageTheme,
              color: "purple",
              value: item,
              onClick: handleClickPage,
              className: twMerge3(
                isActive && "bg-brand-purple-50 text-brand-purple-900"
              ),
              "aria-current": isActive,
              "aria-label": `go to page ${item}`,
              children: formatNumber(Number(item))
            },
            item
          );
        }) }),
        /* @__PURE__ */ jsx23(
          Button7,
          {
            "aria-label": "move-right",
            onClick: handleClickArrow(1),
            disabled: activePage === numberPages || numberPages <= 0,
            outline: true,
            color: "gray",
            theme: arrowButtonTheme,
            children: /* @__PURE__ */ jsxs19("div", { className: "flex gap-2 items-center", children: [
              "\u0421\u043B\u0435\u0434\u0443\u044E\u0449\u0430\u044F",
              /* @__PURE__ */ jsx23(Icon4, { name: "arrow-back", className: "rotate-180" })
            ] })
          }
        )
      ]
    }
  );
};

// src/shared/ui/resultModal/resultModal.tsx
import { Icon as Icon5 } from "@acme/assets";
import { jsx as jsx24, jsxs as jsxs20 } from "react/jsx-runtime";
var ResultModal = (props) => {
  return /* @__PURE__ */ jsxs20("div", { className: "flex flex-col p-1 gap-6 items-center", children: [
    props.type === "success" ? /* @__PURE__ */ jsx24("div", { className: "flex justify-center items-center w-12 h-12 rounded-full bg-green-100", children: /* @__PURE__ */ jsx24(Icon5, { name: "check", color: "#0E9F6E", className: "h-5 w-5" }) }) : /* @__PURE__ */ jsx24("div", { className: "flex justify-center items-center w-12 h-12 rounded-full bg-red-100", children: /* @__PURE__ */ jsx24(Icon5, { name: "x-outline", color: "#F05252", className: "h-5 w-5" }) }),
    /* @__PURE__ */ jsxs20("div", { className: "flex flex-col items-center gap-1", children: [
      /* @__PURE__ */ jsx24("div", { className: "leading-tight text-2xl font-bold text-gray-900 text-center", children: props.title }),
      /* @__PURE__ */ jsx24("div", { className: "text-base font-normal text-gray-500 text-center", children: props.description })
    ] }),
    /* @__PURE__ */ jsxs20(
      "div",
      {
        className: "flex items-center gap-3 text-gray-900 text-sm font-medium cursor-pointer px-5",
        onClick: props.onBack,
        children: [
          /* @__PURE__ */ jsx24(Icon5, { name: "arrow-left", color: "#111928", className: "h-3.5 w-3.5" }),
          /* @__PURE__ */ jsx24("div", { children: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C\u0441\u044F \u0432 \u0440\u0430\u0437\u0434\u0435\u043B" })
        ]
      }
    )
  ] });
};

// src/shared/ui/textAreaModal/textAreaModal.tsx
import { Button as Button8 } from "flowbite-react";
import { useCallback as useCallback4, useState as useState10 } from "react";
import { HiX as HiX2 } from "react-icons/hi";
import { jsx as jsx25, jsxs as jsxs21 } from "react/jsx-runtime";
var TextAreaModal = (props) => {
  const [value, setValue] = useState10("");
  const handleApprove = useCallback4(() => {
    props.onApprove(value);
  }, [value]);
  return /* @__PURE__ */ jsxs21("div", { children: [
    /* @__PURE__ */ jsxs21("div", { className: "flex pb-5", children: [
      /* @__PURE__ */ jsx25("h3", { className: "text-lg font-semibold text-gray-900", children: props.title }),
      /* @__PURE__ */ jsx25(
        "button",
        {
          onClick: props.onClose,
          className: "text-gray-400 hover:text-gray-600 ml-auto",
          children: /* @__PURE__ */ jsx25(HiX2, { className: "h-3 w-3" })
        }
      )
    ] }),
    /* @__PURE__ */ jsx25(Separator, { className: "-mx-5" }),
    /* @__PURE__ */ jsxs21("div", { className: "flex flex-col py-5 gap-2", children: [
      /* @__PURE__ */ jsx25("div", { className: "text-gray-900 text-sm font-medium", children: props.areaLabel }),
      /* @__PURE__ */ jsx25(Textarea, { value, rows: 6, noResize: true, onChange: setValue })
    ] }),
    /* @__PURE__ */ jsx25(Separator, { className: "-mx-5" }),
    /* @__PURE__ */ jsxs21("div", { className: "flex pt-5 gap-4", children: [
      /* @__PURE__ */ jsx25(Button8, { color: "primary", disabled: !value, onClick: handleApprove, children: props.approveButtonText || "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C" }),
      /* @__PURE__ */ jsx25(Button8, { color: "gray", onClick: props.onClose, children: "\u041E\u0442\u043C\u0435\u043D\u0430" })
    ] })
  ] });
};

// src/shared/ui/table/table.tsx
import {
  Table as FlowTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow
} from "flowbite-react";
import { Icon as Icon6 } from "@acme/assets";
import { twMerge as twMerge4 } from "tailwind-merge";
import { jsx as jsx26, jsxs as jsxs22 } from "react/jsx-runtime";
var Table = (props) => {
  return /* @__PURE__ */ jsxs22("div", { className: twMerge4("border rounded-lg relative", props.className), children: [
    /* @__PURE__ */ jsx26("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsxs22(FlowTable, { hoverable: true, children: [
      /* @__PURE__ */ jsx26(TableHead, { children: /* @__PURE__ */ jsx26(TableRow, { children: props.columns.map((item) => {
        return /* @__PURE__ */ jsx26(
          TableHeadCell,
          {
            className: twMerge4(item.sorter && "cursor-pointer"),
            children: /* @__PURE__ */ jsxs22("div", { className: "flex items-center gap-1", children: [
              item.title,
              item.sorter && /* @__PURE__ */ jsx26(Icon6, { name: "chevron-sort", size: 10 })
            ] })
          },
          item.id
        );
      }) }) }),
      /* @__PURE__ */ jsx26(TableBody, { children: props.data.map((entity) => {
        return /* @__PURE__ */ jsx26(
          TableRow,
          {
            className: "text-gray-900 text-sm border-b last:border-b-none border-gray-70",
            children: props.columns.map((config) => {
              const value = entity?.[config.dataIndex];
              return /* @__PURE__ */ jsx26(
                TableCell,
                {
                  width: config?.width,
                  className: config?.className,
                  children: config?.render?.(value, entity) || value
                },
                config.id
              );
            })
          },
          entity[props.keyIndex]
        );
      }) })
    ] }) }),
    props.pagination && /* @__PURE__ */ jsx26(
      Pagination,
      {
        className: "px-6 py-4",
        onPageChange: () => {
          console.log();
        },
        pageNumber: props.pagination?.currentPage,
        pageSize: props.pagination?.pageSize,
        totalCount: props.pagination.totalCount
      }
    )
  ] });
};

// src/shared/ui/datePickerRange/datePickerRange.tsx
import {
  createTheme as createTheme6,
  Datepicker,
  Dropdown as Dropdown5,
  DropdownItem as DropdownItem3,
  TextInput as TextInput6
} from "flowbite-react";
import { Icon as Icon7 } from "@acme/assets";
import { useState as useState11 } from "react";

// src/shared/theme/flowbite.ts
import { createTheme as createTheme5 } from "flowbite-react";
var docsTabsTheme = createTheme5({
  tabs: {
    base: "flex flex-col",
    tablist: {
      base: "flex gap-4 items-center bg-white p-4 rounded-lg w-fit shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]",
      tabitem: {
        base: "flex items-center justify-center px-4 py-2 p-0 rounded-lg text-sm font-normal leading-[1.5] first:ml-0 focus:outline-none disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-500",
        variant: {
          default: {
            base: "px-4 py-2",
            active: {
              on: "bg-brand-green-900 text-white",
              off: "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            }
          }
        },
        icon: "mr-2 h-5 w-5"
      }
    },
    tabpanel: ""
  }
}).tabs;
var dropdownTheme = createTheme5({
  content: "p-2 cursor-pointer",
  floating: {
    base: "z-50 rounded-lg bg-white shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.05)]",
    content: "p-2 gap-1",
    target: "w-full",
    item: {
      base: "flex w-full cursor-pointer items-center justify-start text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white dark:focus:bg-gray-600 dark:focus:text-white",
      icon: "mr-2 h-4 w-4"
    }
  }
});
var textInputTheme = {
  field: {
    input: {
      base: "cursor-pointer"
    }
  }
};
var tableTheme = {
  root: {
    base: "w-full text-left text-sm text-gray-500 dark:text-gray-400 relative z-0",
    shadow: "",
    wrapper: "relative"
  }
};
var customTheme = createTheme5({
  table: tableTheme,
  dropdown: {
    content: "p-0 cursor-pointer rounded-lg overflow-hidden",
    floating: {
      base: "z-50 rounded-lg bg-white shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.05)]",
      content: "p-2 gap-1 ",
      target: "w-full",
      item: {
        base: "flex w-full cursor-pointer items-center justify-start text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white dark:focus:bg-gray-600 dark:focus:text-white",
        icon: "mr-2 h-4 w-4"
      }
    }
  },
  button: {
    size: {
      xs: "h-8 px-2 text-xs",
      sm: "h-9 px-2 text-sm",
      md: "h-10 px-4 text-sm",
      lg: "h-12 px-5 text-base",
      xl: "h-[52px] px-6 text-base"
    },
    color: {
      base: "px-4 py-2 z-10",
      primary: "py-2 bg-brand-purple-900 hover:bg-brand-purple-500 text-white focus:ring-4 focus:ring-brand-purple-100",
      purple: "bg-purple-900 hover:bg-purple-700 text-white focus:ring-4 focus:ring-purple-300",
      gray: "bg-gray-200 hover:bg-gray-300 text-gray-900 focus:ring-4 focus:ring-gray-100",
      alternative: "border-brand-green-500/50 text-brand-green-900 hover:bg-brand-green-50 hover:text-brand-green-900",
      second: "bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 hover:text-gray-900 focus:ring-4 focus:ring-gray-100",
      red: "bg-red-700 text-white"
    },
    outlineColor: {
      primary: "border border-brand-purple-900 text-brand-purple-900 hover:text-brand-purple-900 hover:bg-brand-purple-900 hover:text-white focus:ring-brand-purple-900"
    }
  },
  alert: {
    color: {
      info: "bg-gray-50 text-gray-800 border-gray-200"
    }
  },
  badge: {
    root: {
      color: {
        green: "bg-brand-green-900 text-white font-normal",
        lightGreen: "bg-green-150 text-green-800 font-medium",
        purple: "bg-brand-purple-50 text-brand-purple-900",
        red: "bg-red-100 text-red-800 font-medium"
      }
    }
  },
  card: {
    root: {
      children: "flex h-full flex-col justify-center gap-6 p-6"
    }
  }
});
var adminTheme = createTheme5({
  table: tableTheme,
  button: {
    size: {
      xs: "h-8 px-2 text-xs",
      sm: "h-9 px-2 text-sm",
      md: "h-10 px-4 text-sm",
      lg: "h-12 px-5 text-base",
      xl: "h-[52px] px-6 text-base"
    },
    color: {
      base: "px-4 py-2",
      primary: "py-2 bg-blue-600 hover:bg-blue-700 text-white focus:ring-4 focus:ring-blue-100",
      purple: "bg-blue-600 hover:bg-blue-700 text-white focus:ring-4 focus:ring-blue-300",
      gray: "bg-gray-200 hover:bg-gray-300 text-gray-900 focus:ring-4 focus:ring-gray-100",
      alternative: "border-brand-green-500/50 text-brand-green-900 hover:bg-brand-green-50 hover:text-brand-green-900",
      second: "bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 hover:text-gray-900 focus:ring-4 focus:ring-gray-100"
    }
  },
  alert: {
    color: {
      info: "bg-blue-50 text-blue-800 border-blue-200"
    }
  },
  badge: {
    root: {
      color: {
        green: "bg-gray-800 text-white font-normal"
      }
    }
  }
});

// src/shared/ui/datePickerRange/datePickerRange.tsx
import { twMerge as twMerge5 } from "tailwind-merge";
import { jsx as jsx27, jsxs as jsxs23 } from "react/jsx-runtime";
var currentMonth = new Date(
  (/* @__PURE__ */ new Date()).getFullYear(),
  (/* @__PURE__ */ new Date()).getMonth(),
  1,
  0,
  0,
  0
);
var nextMonth = new Date(currentMonth);
nextMonth.setMonth(currentMonth.getMonth() + 1);
var buttonTheme = customTheme.button.color.primary;
var defaultPeriod = { start: null, end: null };
var datePickerTheme = (type, date) => {
  return createTheme6({
    popup: {
      root: {
        inner: "bg-transparent shadow-none p-0"
      },
      header: {
        selectors: {
          button: {
            view: `capitalize w-[195px] before:text-gray-500 before:font-semibold before:mr-1 ${type === "start" ? "before:content-['\u041E\u0442:']" : "before:content-['\u0414\u043E:']"}`
          }
        }
      },
      view: {
        base: "p-1"
      }
    },
    views: {
      days: {
        header: {
          title: "uppercase"
        },
        items: {
          base: "w-full",
          item: {
            base: `block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600`,
            selected: `text-white bg-brand-purple-900 hover:bg-brand-purple-900 rounded-none ${type === "start" ? "rounded-l-lg" : "rounded-r-lg"} ${!date && "bg-transparent text-gray-900 hover:bg-gray-100 rounded-lg"}`,
            disabled: "text-gray-300 hover:bg-transparent cursor-not-allowed"
          }
        }
      }
    }
  });
};
var DatePickerRange = (props) => {
  const [period, setPeriod] = useState11({
    start: props.value?.start ? new Date(props.value?.start) : null,
    end: props.value?.end ? new Date(String(props.value?.end)) : null
  });
  const [inputValue, setInputValue] = useState11("");
  const handleChangePeriod = (field) => (value) => {
    setPeriod((prev) => ({ ...prev, [field]: value }));
  };
  const handleClearPeriod = (e) => {
    e.stopPropagation();
    setPeriod({ ...defaultPeriod });
    setInputValue("");
    props.onChange(defaultPeriod);
  };
  const handleSetPeriod = () => {
    if (!period.start || !period.end) {
      return;
    }
    setInputValue(
      `${formatDate(period.start).replace(/\./g, "/")} \u2014 ${formatDate(period.end).replace(/\./g, "/")}`
    );
    props.onChange({
      start: period.start.toISOString(),
      end: period.end.toISOString()
    });
  };
  return /* @__PURE__ */ jsx27("div", { children: /* @__PURE__ */ jsxs23(
    Dropdown5,
    {
      theme: {
        content: "grid grid-cols-2 gap-x-8 gap-y-4 p-4 cursor-default",
        floating: {
          item: {
            base: twMerge5(
              buttonTheme,
              "focus:bg-brand-purple-500 relative flex items-center justify-center rounded-lg text-center font-medium focus:outline-none focus:ring-4 disabled:pointer-events-none disabled:opacity-50"
            )
          }
        }
      },
      renderTrigger: () => /* @__PURE__ */ jsx27("div", { className: "relative group", children: /* @__PURE__ */ jsx27(
        TextInput6,
        {
          value: inputValue,
          theme: {
            field: {
              rightIcon: {
                base: "pointer-events-auto absolute inset-y-0 right-0 flex items-center pr-3"
              }
            }
          },
          type: "text",
          readOnly: true,
          placeholder: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043F\u0435\u0440\u0438\u043E\u0434",
          icon: () => /* @__PURE__ */ jsx27(Icon7, { name: "calendar-month", className: "text-gray-500" }),
          rightIcon: () => {
            if (!inputValue) {
              return null;
            }
            return /* @__PURE__ */ jsx27(
              "button",
              {
                type: "button",
                className: "flex items-center justify-center text-gray-500 hover:text-gray-700",
                "aria-label": "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C",
                onClick: handleClearPeriod,
                children: /* @__PURE__ */ jsx27(Icon7, { name: "x-outline", size: 12 })
              }
            );
          }
        }
      ) }),
      children: [
        /* @__PURE__ */ jsx27(
          Datepicker,
          {
            theme: datePickerTheme("start", period.start),
            value: period.start,
            onChange: handleChangePeriod("start"),
            inline: true,
            language: "ru",
            showTodayButton: false,
            showClearButton: false,
            weekStart: 1,
            maxDate: period.end ? new Date(period.end) : void 0
          }
        ),
        /* @__PURE__ */ jsx27(
          Datepicker,
          {
            theme: datePickerTheme("end", period.end),
            value: period.end,
            defaultValue: new Date((/* @__PURE__ */ new Date()).getFullYear(), (/* @__PURE__ */ new Date()).getMonth() + 1),
            onChange: handleChangePeriod("end"),
            inline: true,
            language: "ru",
            showTodayButton: false,
            showClearButton: false,
            weekStart: 1,
            minDate: period.start ? new Date(period.start) : void 0
          }
        ),
        /* @__PURE__ */ jsx27(
          DropdownItem3,
          {
            onClick: handleSetPeriod,
            disabled: !period.start || !period.end,
            children: "\u041F\u0440\u0438\u043C\u0435\u043D\u0438\u0442\u044C"
          }
        )
      ]
    }
  ) });
};

// src/shared/ui/infoAlert/infoAlert.tsx
import { jsx as jsx28, jsxs as jsxs24 } from "react/jsx-runtime";
var variantStyles = {
  info: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-800"
  },
  warning: {
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-800"
  },
  success: {
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-800"
  },
  error: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-800"
  }
};
var InfoAlert = ({
  title,
  description,
  icon,
  variant = "warning",
  className = ""
}) => {
  const styles = variantStyles[variant];
  return /* @__PURE__ */ jsxs24(
    "div",
    {
      className: `${styles.bg} rounded-md p-4 flex flex-col gap-1.5 ${className}`,
      children: [
        /* @__PURE__ */ jsxs24("div", { className: "flex gap-2 items-center", children: [
          icon && /* @__PURE__ */ jsx28("div", { className: "shrink-0 w-4 h-4 flex items-center", children: icon }),
          /* @__PURE__ */ jsx28("p", { className: `${styles.text} text-base font-semibold`, children: title })
        ] }),
        /* @__PURE__ */ jsx28("p", { className: `${styles.text} text-sm`, children: description })
      ]
    }
  );
};

// src/entities/page/index.tsx
import { jsx as jsx29, jsxs as jsxs25 } from "react/jsx-runtime";
var Page = ({ children, breadcrumbItems, isLoading }) => {
  return /* @__PURE__ */ jsxs25("div", { className: "flex flex-col gap-6", children: [
    /* @__PURE__ */ jsx29(Breadcrumb, { items: breadcrumbItems }),
    isLoading ? /* @__PURE__ */ jsx29("div", { className: "rounded-lg bg-white p-6 shadow", children: /* @__PURE__ */ jsx29("div", { className: "text-center text-gray-600", children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026" }) }) : children
  ] });
};

// src/widgets/form/model/createFormModel.ts
import { useCallback as useCallback5, useMemo as useMemo3, useReducer } from "react";

// src/widgets/form/lib/path.ts
var getByPath = (obj, path) => {
  if (!path)
    return void 0;
  return path.split(".").reduce(
    (o, k) => o != null ? o[k] : void 0,
    obj
  );
};
var setByPath = (obj, path, value) => {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const next = cur[key];
    cur[key] = next != null && typeof next === "object" && !Array.isArray(next) ? next : {};
    cur = cur[key];
  }
  cur[parts.at(-1)] = value;
};

// src/widgets/form/model/mapValuesForWrite.ts
function mapValuesForWrite(controls, values, ctx, includeUnchangedFields = false) {
  let out = includeUnchangedFields ? structuredClone(ctx) : {};
  for (const c of controls) {
    if (!c.write)
      continue;
    let v = values[c.id];
    if (v === void 0) {
      if (typeof c.read === "function") {
        v = c.read(ctx);
      } else if (typeof c.read === "string") {
        v = getByPath(ctx, c.read);
      }
    }
    if (typeof c.write === "function") {
      out = c.write(v, out);
    } else {
      const tv = c.write.transform ? c.write.transform(v, out) : v;
      if (tv === void 0)
        continue;
      setByPath(out, c.write.path, tv);
    }
  }
  return out;
}

// src/widgets/form/model/reducer.ts
var initialState = {
  values: {},
  errors: {},
  touched: {},
  submitCount: 0,
  isSubmitting: false
};
function formReducer(state, action) {
  switch (action.type) {
    case "fieldChanged":
      return {
        ...state,
        values: { ...state.values, [action.id]: action.value }
      };
    case "fieldBlurred":
      return { ...state, touched: { ...state.touched, [action.id]: true } };
    case "setErrors":
      return { ...state, errors: action.errors };
    case "submitting":
      return { ...state, isSubmitting: action.value };
    case "incrementSubmit":
      return { ...state, submitCount: state.submitCount + 1 };
    case "reset":
      return { ...initialState, ...action.next };
    default:
      return state;
  }
}

// src/widgets/form/model/rules.ts
function evalRules(rules, bag) {
  const acc = {
    hidden: false,
    disabled: false,
    required: false,
    readOnly: false
  };
  if (!rules)
    return acc;
  for (const r of rules) {
    const ok = typeof r.when === "function" ? r.when(bag) : safeEval(r.when, bag);
    if (ok && r.set)
      Object.assign(acc, r.set);
  }
  return acc;
}
function safeEval(expr, bag) {
  try {
    const fn = new Function("values", "ctx", `return (${expr});`);
    return fn(bag.values, bag.ctx);
  } catch {
    return false;
  }
}

// src/widgets/form/model/createFormModel.ts
var makeValidate = (controls, schema) => (values, ctx, state) => {
  const showValidation = state.submitCount > 0 || Object.keys(state.touched).length > 0;
  const errs = {};
  for (const c of controls) {
    if (typeof c.validation === "function") {
      errs[c.id] = c.validation(values[c.id], { values, ctx, showValidation });
    }
  }
  if (schema) {
    const mapped = mapValuesForWrite(controls, values, ctx, true);
    const res = schema.safeParse(mapped);
    if (!res.success) {
      for (const issue of res.error.issues) {
        const key = String(issue.path[0]);
        errs[key] ||= issue.message;
      }
    }
  }
  return errs;
};
function useFormModel({
  config,
  ctx
}) {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const validate = useMemo3(
    () => makeValidate(config.controls, config.validation),
    [config.controls, config.validation]
  );
  const runtimeById = useMemo3(() => {
    const bag = { values: state.values, ctx };
    const acc = {};
    for (const c of config.controls)
      acc[c.id] = evalRules(c.rules, bag);
    return acc;
  }, [state.values, ctx, config.controls]);
  const onChange = useCallback5((id, value) => {
    dispatch({ type: "fieldChanged", id, value });
  }, []);
  const onBlur = useCallback5((id) => {
    dispatch({ type: "fieldBlurred", id });
  }, []);
  const runValidate = useCallback5(() => {
    const errors = validate(state.values, ctx, state);
    dispatch({ type: "setErrors", errors });
    return errors;
  }, [validate, state.values, state.touched, state.submitCount, ctx]);
  const submit = useCallback5(
    async (handler) => {
      dispatch({ type: "incrementSubmit" });
      const errors = runValidate();
      if (Object.values(errors).some(Boolean))
        return;
      try {
        dispatch({ type: "submitting", value: true });
        const finalModel = mapValuesForWrite(
          config.controls,
          state.values,
          ctx,
          config.includeUnchangedFields ?? false
        );
        await handler(finalModel);
      } finally {
        dispatch({ type: "submitting", value: false });
      }
    },
    [config.controls, state.values, ctx, runValidate]
  );
  const reset = useCallback5(() => dispatch({ type: "reset" }), []);
  return {
    state,
    runtimeById,
    onChange,
    onBlur,
    runValidate,
    submit,
    reset,
    config,
    ctx
  };
}

// src/widgets/form/ui/form.tsx
import { jsx as jsx30 } from "react/jsx-runtime";
function Form({
  form,
  className,
  children,
  onSubmit
}) {
  return /* @__PURE__ */ jsx30(
    "form",
    {
      className,
      onSubmit: async (e) => {
        e.preventDefault();
        await form.submit(async (finalModel) => {
          if (onSubmit)
            await onSubmit(finalModel);
        });
      },
      children
    }
  );
}

// src/widgets/form/ui/formControls.tsx
import { twMerge as twMerge6 } from "tailwind-merge";
import { Fragment as Fragment3, useMemo as useMemo4 } from "react";

// src/widgets/form/lib/template.ts
var applyTemplate = (tpl, obj) => tpl.replace(/\{\{(.*?)\}\}/g, (_, key) => String(obj[key.trim()] ?? ""));

// src/widgets/form/ui/adapters/flowbite/controls.tsx
import { Checkbox, Datepicker as Datepicker2, Label, Select as Select2, TextInput as TextInput7 } from "flowbite-react";
import { useEffect as useEffect3, useRef as useRef2, useState as useState12 } from "react";
import { useCallback as useCallback6 } from "react";
import { HiInformationCircle } from "react-icons/hi";
import { jsx as jsx31, jsxs as jsxs26 } from "react/jsx-runtime";
function FileUploadControl({
  value,
  onChange,
  props
}) {
  const list = Array.isArray(value) ? value : [];
  const accept = props?.accept ?? ".pdf";
  const maxSize = props?.maxSize ?? 10;
  const multiple = props?.multiple ?? true;
  const showInfoIcon = props?.showInfoIcon ?? false;
  const label = props?.label ?? "";
  const handleFileSelect = useCallback6(
    (selected) => {
      const newItems = selected.map((file, index) => ({
        id: `fu-${Date.now()}-${index}`,
        file,
        status: "pending",
        progress: 0
      }));
      onChange([...list, ...newItems]);
    },
    [list, onChange]
  );
  const handleFileRemove = useCallback6(
    (fileId) => {
      onChange(list.filter((f) => f.id !== fileId));
    },
    [list, onChange]
  );
  return /* @__PURE__ */ jsxs26("div", { className: "space-y-2", children: [
    (label || showInfoIcon) && /* @__PURE__ */ jsxs26("div", { className: "flex items-center gap-2 mb-1", children: [
      label ? /* @__PURE__ */ jsx31("span", { className: "text-sm font-medium text-gray-900", children: label }) : null,
      showInfoIcon ? /* @__PURE__ */ jsx31(HiInformationCircle, { className: "w-4 h-4 text-green-500 shrink-0" }) : null
    ] }),
    /* @__PURE__ */ jsx31(
      FileUpload,
      {
        onFileSelect: handleFileSelect,
        onFileRemove: handleFileRemove,
        uploadingFiles: list,
        accept,
        maxSize,
        multiple
      }
    )
  ] });
}
var FlowbiteControls = {
  text: ({ value, onChange, onBlur, props }) => /* @__PURE__ */ jsx31(
    TextInput7,
    {
      value: value ?? "",
      onChange: (e) => onChange(e.target.value),
      onBlur,
      ...props
    }
  ),
  textarea: ({ value, onChange, onBlur, props }) => /* @__PURE__ */ jsx31(
    Textarea,
    {
      value: value ?? "",
      onChange: (value2) => onChange(value2),
      onBlur,
      noResize: true,
      ...props
    }
  ),
  number: ({ value, onChange, onBlur, props }) => /* @__PURE__ */ jsx31(
    TextInput7,
    {
      type: "number",
      value: value ?? "",
      onChange: (e) => onChange(e.target.value),
      onBlur,
      ...props
    }
  ),
  checkbox: ({ value, onChange, onBlur, props }) => /* @__PURE__ */ jsxs26("div", { className: "flex items-center gap-2", children: [
    /* @__PURE__ */ jsx31(
      Checkbox,
      {
        checked: !!value,
        onChange: (e) => onChange(e.target.checked),
        onBlur,
        ...props
      }
    ),
    props?.label && /* @__PURE__ */ jsx31(Label, { htmlFor: props.id, children: props.label })
  ] }),
  datepicker: ({ value, onChange, onBlur, props }) => {
    const [showPicker, setShowPicker] = useState12(false);
    const containerRef = useRef2(null);
    useEffect3(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setShowPicker(false);
        }
      };
      if (showPicker) {
        document.addEventListener("mousedown", handleClickOutside);
      } else {
        document.removeEventListener("mousedown", handleClickOutside);
      }
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showPicker]);
    const formatDate2 = (value2) => {
      let date;
      if (typeof value2 === "string") {
        date = new Date(value2);
        if (isNaN(date.getTime())) {
          return null;
        }
      } else {
        date = value2;
      }
      const formatter = new Intl.DateTimeFormat("ru-RU", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
      });
      return formatter.format(date);
    };
    const handleDateSelection = (date) => {
      if (date) {
        onChange(date);
      }
      setShowPicker(false);
    };
    return /* @__PURE__ */ jsxs26("div", { className: "relative w-full", ref: containerRef, children: [
      /* @__PURE__ */ jsx31(
        TextInput7,
        {
          className: "w-full",
          onFocus: () => setShowPicker(true),
          value: formatDate2(value) ?? "",
          onBlur,
          ...props
        }
      ),
      showPicker && /* @__PURE__ */ jsx31(
        Datepicker2,
        {
          language: "ru-RU",
          weekStart: 1,
          value: null,
          onChange: handleDateSelection,
          className: "w-full absolute z-50",
          showClearButton: false,
          showTodayButton: false,
          defaultValue: value ? new Date(value) : /* @__PURE__ */ new Date(),
          autoHide: true,
          readOnly: true,
          inline: true
        }
      )
    ] });
  },
  select: ({ value, onChange, onBlur, props, options }) => /* @__PURE__ */ jsxs26(
    Select2,
    {
      value: value ?? "",
      onChange: (e) => onChange(e.target.value),
      onBlur,
      ...props,
      children: [
        /* @__PURE__ */ jsx31("option", { value: "", disabled: true, children: props?.placeholder ?? "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435\u2026" }),
        options?.map((o) => /* @__PURE__ */ jsx31("option", { value: o.value, children: o.label }, String(o.value)))
      ]
    }
  ),
  fileUpload: ({ value, onChange, props }) => /* @__PURE__ */ jsx31(FileUploadControl, { value, onChange, props })
};

// src/widgets/form/ui/formControls.tsx
import { Fragment as Fragment4, jsx as jsx32, jsxs as jsxs27 } from "react/jsx-runtime";
function normalizeOptions(cfg, list) {
  const opts = cfg?.options;
  if (opts?.mapKeys) {
    const { labelKey, valueKey } = opts.mapKeys;
    return list.map((item) => ({
      label: String(item[labelKey] ?? ""),
      value: item[valueKey]
    }));
  }
  if (opts?.template) {
    const tpl = opts.template;
    return list.map((item) => ({
      label: applyTemplate(tpl.label, item),
      value: applyTemplate(tpl.value, item)
    }));
  }
  return list.map((item) => {
    const o = item;
    return {
      label: String(o.label ?? ""),
      value: o.value ?? ""
    };
  });
}
function FormControls({
  form,
  filter
}) {
  const { controls } = form.config;
  return /* @__PURE__ */ jsx32(Fragment4, { children: controls.filter((c) => filter ? filter(c) : true).map((cfg) => /* @__PURE__ */ jsx32(Fragment3, { children: /* @__PURE__ */ jsx32(Field2, { form, cfg }) }, cfg.id)) });
}
function Field2({
  form,
  cfg
}) {
  const { state, runtimeById, onChange, onBlur, ctx } = form;
  const runtime = runtimeById[cfg.id] || {};
  const value = state.values[cfg.id] ?? (typeof cfg.read === "function" ? cfg.read(ctx) : getByPath(ctx, cfg.read)) ?? (cfg.type === "checkbox" ? false : "");
  const hasError = state.errors[cfg.id];
  const isTouched = state.touched[cfg.id];
  const showError = hasError && (isTouched || state.submitCount > 0);
  const error = showError ? state.errors[cfg.id] ?? null : null;
  const options = useMemo4(() => {
    if (cfg.type !== "select")
      return void 0;
    const src = cfg.options?.source;
    const rawList = typeof src === "function" ? src(ctx) : Array.isArray(src) ? src : [];
    const list = Array.isArray(rawList) ? rawList : [];
    return normalizeOptions(cfg, list);
  }, [cfg, ctx]);
  if (runtime.hidden)
    return null;
  if (cfg.type === "customHtml") {
    return /* @__PURE__ */ jsx32("div", { className: "col-span-2", children: cfg.template?.({ values: state.values, ctx }) });
  }
  if (cfg.type === "customControl") {
    const Comp = form.config._registry?.[cfg.component ?? ""];
    return /* @__PURE__ */ jsxs27("div", { className: "flex flex-col gap-1", children: [
      cfg.label && /* @__PURE__ */ jsxs27("label", { className: "text-sm", children: [
        cfg.label,
        runtime.required && /* @__PURE__ */ jsx32("span", { className: "text-red-600", children: " *" })
      ] }),
      Comp ? /* @__PURE__ */ jsx32(
        Comp,
        {
          value,
          onChange: (v) => onChange(cfg.id, v),
          onBlur: () => onBlur(cfg.id),
          ...cfg.props
        }
      ) : null,
      error && /* @__PURE__ */ jsx32("p", { className: "text-sm text-red-600", children: error })
    ] });
  }
  const flowbiteKey = cfg.type;
  const Render = FlowbiteControls[flowbiteKey];
  if (!Render)
    return null;
  const props = cfg.props;
  const className = typeof props?.className === "string" ? props.className : void 0;
  const placeholder = typeof props?.placeholder === "string" ? props.placeholder : void 0;
  const label = typeof props?.label === "string" ? props.label : void 0;
  const onChangeProp = props?.onChange;
  const renderOuterLabel = cfg.type !== "checkbox" && cfg.type !== "fileUpload";
  const labelForControl = cfg.type === "checkbox" ? cfg.label : label;
  return /* @__PURE__ */ jsxs27("div", { className: twMerge6("flex flex-col gap-1", className), children: [
    renderOuterLabel && cfg.label && /* @__PURE__ */ jsxs27("label", { className: "text-sm", children: [
      cfg.label,
      runtime.required && /* @__PURE__ */ jsx32("span", { className: "text-red-600", children: " *" })
    ] }),
    /* @__PURE__ */ jsx32(
      Render,
      {
        value,
        onChange: (v) => {
          if (typeof onChangeProp === "function") {
            onChangeProp(v);
          }
          onChange(cfg.id, v);
        },
        onBlur: () => onBlur(cfg.id),
        options,
        props: {
          disabled: runtime.disabled,
          readOnly: runtime.readOnly,
          required: runtime.required,
          placeholder,
          label: labelForControl,
          id: cfg.id
        }
      }
    ),
    error && /* @__PURE__ */ jsx32("p", { className: "text-sm text-red-600", children: error }),
    cfg.helperText && /* @__PURE__ */ jsx32("p", { className: "text-xs text-gray-500", children: cfg.helperText })
  ] });
}

// src/widgets/groupedList/groupedList.tsx
import { twMerge as twMerge7 } from "tailwind-merge";
import { jsx as jsx33, jsxs as jsxs28 } from "react/jsx-runtime";
var GroupedList = ({
  groups,
  renderItem,
  renderGroupHeader,
  emptyMessage = "\u041D\u0435\u0442 \u044D\u043B\u0435\u043C\u0435\u043D\u0442\u043E\u0432",
  className = ""
}) => {
  const hasItems = groups.some((group) => group.items.length > 0);
  if (!hasItems) {
    return /* @__PURE__ */ jsx33("div", { className: `flex items-center justify-center py-8 ${className}`, children: /* @__PURE__ */ jsx33("p", { className: "text-sm text-gray-500", children: emptyMessage }) });
  }
  const defaultRenderGroupHeader = (group) => /* @__PURE__ */ jsx33("h2", { className: "text-xl font-normal text-gray-500 leading-4", children: group.label });
  return /* @__PURE__ */ jsx33("div", { className: `flex flex-col gap-4 ${className}`, children: groups.map((group) => {
    if (group.items.length === 0) {
      return null;
    }
    return /* @__PURE__ */ jsxs28("div", { className: "flex flex-col gap-4", children: [
      renderGroupHeader ? renderGroupHeader(group) : defaultRenderGroupHeader(group),
      /* @__PURE__ */ jsx33("div", { className: twMerge7("flex flex-col gap-4", group.className), children: group.items.map((item, index) => /* @__PURE__ */ jsx33("div", { children: renderItem(item) }, index)) })
    ] }, group.key);
  }) });
};

// src/widgets/paginatedContent/paginatedContent.tsx
import { jsx as jsx34, jsxs as jsxs29 } from "react/jsx-runtime";
var PaginatedContent = ({
  pageNumber,
  pageSize,
  totalCount,
  onPageChange,
  isLoading = false,
  children,
  className = "",
  classNamePagination
}) => {
  return /* @__PURE__ */ jsxs29("div", { className: `flex flex-col gap-4 ${className}`, children: [
    children,
    /* @__PURE__ */ jsx34(
      Pagination,
      {
        pageNumber,
        pageSize,
        totalCount,
        onPageChange,
        isLoading,
        className: classNamePagination
      }
    )
  ] });
};

// src/widgets/paginatedList/paginatedList.tsx
import { useEffect as useEffect4, useMemo as useMemo5, useState as useState13 } from "react";
import { Fragment as Fragment5, jsx as jsx35 } from "react/jsx-runtime";
function PaginatedList({
  method,
  params,
  extract,
  initialPage = 1,
  pageSize = 20,
  className = "",
  loadingFallback,
  onMetaChange,
  children,
  classNamePagination
}) {
  const [items, setItems] = useState13([]);
  const [totalCount, setTotalCount] = useState13(0);
  const [pageNumber, setPageNumber] = useState13(initialPage);
  const [isLoading, setIsLoading] = useState13(false);
  const paramsKey = useMemo5(() => JSON.stringify(params ?? {}), [params]);
  useEffect4(() => {
    setPageNumber(initialPage);
  }, [initialPage, paramsKey]);
  useEffect4(() => {
    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      try {
        const response = await method({
          ...params ?? {},
          pageNumber,
          pageSize
        });
        if (cancelled)
          return;
        const data = extract(response);
        const nextItems = data.items ?? [];
        const nextTotalCount = data.totalCount ?? nextItems.length;
        setItems(nextItems);
        setTotalCount(nextTotalCount);
      } finally {
        if (!cancelled)
          setIsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [method, extract, paramsKey, pageNumber, pageSize]);
  useEffect4(() => {
    onMetaChange?.({
      items,
      totalCount,
      pageNumber,
      pageSize,
      isLoading
    });
  }, [items, totalCount, pageNumber, pageSize, isLoading, onMetaChange]);
  if (isLoading && loadingFallback) {
    return /* @__PURE__ */ jsx35(Fragment5, { children: loadingFallback });
  }
  return /* @__PURE__ */ jsx35(
    PaginatedContent,
    {
      pageNumber,
      pageSize,
      totalCount,
      onPageChange: setPageNumber,
      isLoading,
      className,
      classNamePagination,
      children: children(items)
    }
  );
}

// src/widgets/container/ui/dataContainer/sample.tsx
import { Image as Image2 } from "@acme/assets";
import { twMerge as twMerge8 } from "tailwind-merge";
import { Button as Button9 } from "flowbite-react";
import { Fragment as Fragment6, jsx as jsx36, jsxs as jsxs30 } from "react/jsx-runtime";
var Sample = (props) => {
  return /* @__PURE__ */ jsx36(
    "div",
    {
      className: twMerge8(
        "flex-1 mx-auto flex justify-center items-center gap-6 flex-col",
        props.className
      ),
      children: props.children || /* @__PURE__ */ jsxs30(Fragment6, { children: [
        props.imageName && /* @__PURE__ */ jsx36(Image2, { name: props.imageName, alt: props.imageName }),
        /* @__PURE__ */ jsxs30("div", { className: "text-center", children: [
          props.title && /* @__PURE__ */ jsx36("span", { className: "font-medium", children: props.title }),
          props.message && /* @__PURE__ */ jsx36("div", { className: "text-gray-500", children: props.message })
        ] }),
        Boolean(props.button) && /* @__PURE__ */ jsx36(
          Button9,
          {
            color: "primary",
            onClick: props.button?.onClick,
            className: twMerge8("h-10"),
            children: props.button?.title
          }
        )
      ] })
    }
  );
};

// src/widgets/container/ui/dataContainer/dataContainer.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
var DataContainer = (props) => {
  if (props.loading) {
    return /* @__PURE__ */ jsx37(Sample, { className: props.className, children: /* @__PURE__ */ jsx37("span", { className: "text-primary-900 inline-flex items-end gap-1", children: "Loading..." }) });
  }
  if (props.error?.view) {
    return /* @__PURE__ */ jsx37(
      Sample,
      {
        imageName: props.error.imageName || "profiling",
        title: props.error.title || "\u041E\u0448\u0438\u0431\u043A\u0430",
        message: props.error.message,
        button: props.error.button,
        className: props.className
      }
    );
  }
  if (props.info?.view) {
    return /* @__PURE__ */ jsx37(
      Sample,
      {
        imageName: props.info.imageName || "new-chat",
        title: props.info.title,
        message: props.info.message,
        button: props.info.button,
        className: props.className
      }
    );
  }
  if (props.empty?.view) {
    return /* @__PURE__ */ jsx37(
      Sample,
      {
        imageName: props.empty?.imageName || "new-chat",
        title: props.empty.title,
        message: props.empty.message,
        button: props.empty.button,
        className: props.className
      }
    );
  }
  return props.children;
};

// src/shared/validation/inn.ts
var validateRussianINN = (inn) => {
  if (!inn)
    return null;
  const cleanINN = inn.replace(/\D/g, "");
  if (cleanINN.length !== 10 && cleanINN.length !== 12) {
    return "\u0418\u041D\u041D \u0434\u043E\u043B\u0436\u0435\u043D \u0441\u043E\u0434\u0435\u0440\u0436\u0430\u0442\u044C 10 \u0438\u043B\u0438 12 \u0446\u0438\u0444\u0440";
  }
  if (!/^\d+$/.test(cleanINN)) {
    return "\u0418\u041D\u041D \u0434\u043E\u043B\u0436\u0435\u043D \u0441\u043E\u0434\u0435\u0440\u0436\u0430\u0442\u044C \u0442\u043E\u043B\u044C\u043A\u043E \u0446\u0438\u0444\u0440\u044B";
  }
  return null;
};

// src/shared/validation/snils.ts
var validateRussianSNILS = (snils) => {
  if (!snils)
    return null;
  const cleanSNILS = snils.replace(/\D/g, "");
  if (cleanSNILS.length !== 11) {
    return "\u0421\u041D\u0418\u041B\u0421 \u0434\u043E\u043B\u0436\u0435\u043D \u0441\u043E\u0434\u0435\u0440\u0436\u0430\u0442\u044C 11 \u0446\u0438\u0444\u0440";
  }
  if (!/^\d+$/.test(cleanSNILS)) {
    return "\u0421\u041D\u0418\u041B\u0421 \u0434\u043E\u043B\u0436\u0435\u043D \u0441\u043E\u0434\u0435\u0440\u0436\u0430\u0442\u044C \u0442\u043E\u043B\u044C\u043A\u043E \u0446\u0438\u0444\u0440\u044B";
  }
  const checkSum = parseInt(cleanSNILS.slice(9, 11));
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanSNILS[i]) * (9 - i);
  }
  let expectedCheckSum = sum % 101;
  if (expectedCheckSum === 100 || expectedCheckSum === 0) {
    expectedCheckSum = 0;
  }
  if (checkSum !== expectedCheckSum) {
    return "\u041D\u0435\u0432\u0435\u0440\u043D\u0430\u044F \u043A\u043E\u043D\u0442\u0440\u043E\u043B\u044C\u043D\u0430\u044F \u0441\u0443\u043C\u043C\u0430 \u0421\u041D\u0418\u041B\u0421";
  }
  return null;
};

// src/shared/apiError/types.ts
function isObjectWithKey(value, key) {
  return typeof value === "object" && value !== null && key in value;
}
function isApiErrorBody(value) {
  return isObjectWithKey(value, "errorCode") || isObjectWithKey(value, "errorText");
}

// src/shared/apiError/createApiError.ts
function createApiError(message, body) {
  const err = new Error(message);
  if (isApiErrorBody(body)) {
    err.errorCode = body.errorCode ?? void 0;
    err.errorDetails = body.errorDetails ?? null;
  } else {
    err.errorCode = void 0;
    err.errorDetails = null;
  }
  return err;
}
function createApiErrorFromCaught(caught, fallbackMessage) {
  const body = typeof caught === "object" && caught !== null && "body" in caught && caught.body !== void 0 ? caught.body : null;
  if (body === null || body === void 0) {
    return null;
  }
  const message = isApiErrorBody(body) ? body.errorText ? String(body.errorText) : null : null;
  const err = createApiError(
    message || (caught instanceof Error ? caught.message : fallbackMessage),
    body
  );
  if (typeof caught === "object" && caught !== null && "status" in caught && typeof caught.status === "number" && err.errorCode === void 0) {
    err.errorCode = caught.status;
  }
  return err;
}
export {
  BadRequestAlert,
  Breadcrumb,
  CopyableText,
  CustomSelectDropdown,
  DataContainer,
  DatePickerRange,
  DropdownSelect,
  ErrorPage,
  Field,
  FileAttachment,
  FileAttachmentList,
  FileUpload,
  FilterDropdown,
  Form,
  FormControls,
  GroupedList,
  InfoAlert,
  Input,
  ModalContext,
  ModalProvider,
  Page,
  PaginatedContent,
  PaginatedList,
  Pagination,
  PaginationWithEllipsis,
  PasswordInput,
  PortalDropdown,
  RangeDropdown,
  RangeDropdownFilter,
  ResultModal,
  Select,
  Separator,
  Skeleton,
  Table,
  Tabs,
  TextAreaModal,
  Textarea,
  adminTheme,
  createApiError,
  createApiErrorFromCaught,
  customTheme,
  deepMerge,
  docsTabsTheme,
  downloadFile,
  dropdownTheme,
  formatAddress,
  formatDate,
  formatDateTime,
  formatMonthYear,
  formatNumber,
  formatRelativeDayLabel,
  getByPath,
  getCurrencySymbol,
  hasText,
  isApiErrorBody,
  isObject,
  processFormData,
  renderFieldIfText,
  textInputTheme,
  useFormModel,
  useModal,
  usePortalDropdown,
  validateRussianINN,
  validateRussianSNILS
};
//# sourceMappingURL=index.js.map